import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Apartment, ApartmentDeleteSummary, getApartmentById, updateApartment, deleteApartment, getApartmentDeleteSummary, clearTtlockPins, runTtlockGenerationJob, testUbyportCredentials, UbyportCredentialCheck } from '../api.apartments';
import { getMyContacts, Contact } from '../api.contacts';
import { getCityTaxMailboxes, CityTaxMailbox } from '../api.cityTaxMailboxes';
import { AdvancedDetails, HelpDetails } from '../components/AdvancedDetails';
import UpsellSettings from '../components/UpsellSettings';
import DepositSettings from '../components/DepositSettings';
import { PaymentMethod, listPaymentMethods } from '../api.settings';
import { invalidPaymentMethodPurpose, withPaymentMethodPayload, PaymentPurpose } from '../utils/apartmentPaymentMethods';
import { PlatformFeeSettings, getPlatformFee } from '../api.platformFee';
import CalendarManager from '../components/CalendarManager';
import { DateRangePicker } from '../components/DateRangePicker';
import SectionLoader from '../components/SectionLoader';
import DirectBookingTab from './DirectBookingTab';
import {
  DorucenkaDownloadJob,
  DorucenkaJobStatus,
  downloadDorucenkaZip,
  getCurrentDorucenkaDownloadJob,
  requestDorucenkaDownload,
} from '../api.dorucenkaDownload';
import {
  CheckinGuideConfig,
  GuidePhoto,
  emptyGuideConfig,
  getCheckinGuideConfig,
  saveCheckinGuideConfig,
  uploadGuidePhoto,
  deleteGuidePhoto,
  reorderGuidePhotos,
  OtherLink,
} from '../api.checkinGuide';
import GuidePreviewModal from '../components/GuidePreviewModal';
import GuideSampleControl from './apartmentEdit/GuideSampleControl';
import { withoutUneditedSamples } from './apartmentEdit/guideSamples';
import { guidePreviewText } from '../i18n/pages/guidePreview';
import { fetchExportData, fetchUbyportLookup, downloadCsv } from '../api.export';
import { getTtlockLocks, TtlockLock } from '../api.ttlock';
import { generateExportPdf } from '../utils/generateExportPdf';
import { useLang } from '../i18n/LanguageContext';
import { apartmentDetailText } from '../i18n/pages/apartmentDetail';
import useIsMobile from '../hooks/useIsMobile';
import { apartmentEditNavText } from '../i18n/pages/apartmentEditNav';
import './apartmentEdit/apartmentEdit.css';
import GroupTabs, { GroupBadge, TAB_PANEL_ID, groupLabel, tabDomId } from './apartmentEdit/GroupTabs';
import SetupChecklist from './apartmentEdit/SetupChecklistPanel';
import SettingsSearch from './apartmentEdit/SettingsSearch';
import SaveBar from './apartmentEdit/SaveBar';
import { JumpRequest, jumpTo, sectionDomId } from './apartmentEdit/jumpTo';
import {
  BOOKING_VIEW, GROUPS_WITH_SAVE_BAR, GROUP_ORDER, GroupKey, SECTION_GROUP, SectionKey, TabKey, ViewKey,
  resolveLocation, resolveTabParam, scrollRequestFor, stripLeadingSymbols,
} from './apartmentEdit/groups';
import { deriveChecklist, groupsNeedingAttention, summarizeChecklist } from './apartmentEdit/setupChecklist';
import { dirtySections } from './apartmentEdit/dirty';
import {
  autoSendErrors, withSendableAutoSend, parseMaxGuestsInput, maxGuestsInputValue, maxGuestsLimit,
  loadedMaxGuests, autoSendValuesOnSwitchOn, isAutoSendDefault, withStoredMaxGuests, isValidMaxGuests,
  AUTO_SEND_DELAY_MAX_DAYS,
} from './apartmentEdit/ubyportAutoSend';
import { useChecklistFacts } from './apartmentEdit/useChecklistFacts';

const getToken = () => localStorage.getItem('token') || '';

const ACTIVE_JOB_STATUSES: DorucenkaJobStatus[] = ['PENDING', 'PROCESSING'];

const formatLocalDate = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString();
const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

/**
 * Counts down from `seconds` while `active` is true, for a busy-button label. 30s is only a
 * rough estimate of how long a TTLock cloud round-trip takes — once it elapses without the
 * request finishing, the countdown is meaningless, so the caller should fall back to a plain
 * "…" rather than showing 0s or a negative number.
 */
const useBusyCountdown = (active: boolean, seconds = 30) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (!active) {
      setRemaining(seconds);
      return;
    }
    setRemaining(seconds);
    const interval = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(interval);
  }, [active, seconds]);
  return remaining;
};

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};
const sixYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 6);
  return d.toISOString().slice(0, 10);
};

type GuideEnFieldKey = 'addressRemark' | 'keyRemark' | 'parkingInfo' | 'detailedGuide' | 'attentionNotes' | 'checkoutInstructions';

/** Collapsible optional English-version textarea shown under a guide content field. */
const GuideEnglishField: React.FC<{
  fieldKey: GuideEnFieldKey;
  rows: number;
  guide: CheckinGuideConfig;
  setGuide: React.Dispatch<React.SetStateAction<CheckinGuideConfig>>;
  openFields: Record<string, boolean>;
  setOpenFields: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  addLabel: string;
  fieldLabel: string;
  clearLabel: string;
}> = ({ fieldKey, rows, guide, setGuide, openFields, setOpenFields, addLabel, fieldLabel, clearLabel }) => {
  const enKey = `${fieldKey}En` as const;
  const value = guide[enKey] || '';
  const isOpen = openFields[fieldKey] ?? !!value;

  if (!isOpen) {
    return (
      <button type="button" onClick={() => setOpenFields(o => ({ ...o, [fieldKey]: true }))}
        style={{ marginTop: 6, background: 'none', border: 'none', color: '#2563eb', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
        {addLabel}
      </button>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <label className="form-label" style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 0 }}>{fieldLabel}</label>
        <button type="button"
          onClick={() => { setOpenFields(o => ({ ...o, [fieldKey]: false })); setGuide(g => ({ ...g, [enKey]: '' })); }}
          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
          {clearLabel}
        </button>
      </div>
      <textarea className="form-control" rows={rows} value={value}
        onChange={e => setGuide(g => ({ ...g, [enKey]: e.target.value }))} />
    </div>
  );
};

// The Ubyport "Advanced settings" disclosure: closed by default, opened when a save is refused for a
// value inside it. Looked up by id because AdvancedDetails is a plain native <details>.
const AUTO_SEND_ADVANCED_ID = 'ae-autoSendAdvanced';
const openAutoSendAdvanced = () => {
  const el = document.getElementById(AUTO_SEND_ADVANCED_ID);
  if (el instanceof HTMLDetailsElement) el.open = true;
};

const ApartmentDetail: React.FC = () => {
  const { lang } = useLang();
  const t = apartmentDetailText[lang];
  const isMobile = useIsMobile();
  const { id } = useParams<{ id: string }>();
  const apartmentId = Number(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const returnTo = searchParams.get('returnTo') || '/apartments';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [mailboxes, setMailboxes] = useState<CityTaxMailbox[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [platformFee, setPlatformFee] = useState<PlatformFeeSettings | null>(null);
  const [form, setForm] = useState<Apartment | null>(null);
  const [contactSource, setContactSource] = useState<'manager' | 'list' | 'other'>('manager');
  const [contactEditForm, setContactEditForm] = useState<Contact | null>(null);

  // ── Delete modal state ────────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSummary, setDeleteSummary] = useState<ApartmentDeleteSummary | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ── Check-in guide state ───────────────────────────────────────────────────
  const [guide, setGuide] = useState<CheckinGuideConfig>(emptyGuideConfig());
  const [guideEnFieldsOpen, setGuideEnFieldsOpen] = useState<Record<string, boolean>>({});
  const [guideLoaded, setGuideLoaded] = useState(false);
  const [guidePreviewOpen, setGuidePreviewOpen] = useState(false);
  const [guideSaving, setGuideSaving] = useState(false);
  const [guideError, setGuideError] = useState<string | null>(null);
  const [guideSuccess, setGuideSuccess] = useState<string | null>(null);
  // Feedback floats independently of page layout; neither success nor failure moves focus/scroll.
  useEffect(() => {
    if (!error && !success && !guideError && !guideSuccess) return;
    const timer = window.setTimeout(() => {
      setError(null); setSuccess(null); setGuideError(null); setGuideSuccess(null);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [error, success, guideError, guideSuccess]);

  // ── Guide photos state ────────────────────────────────────────────────────
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoCaption, setPhotoCaption] = useState('');

  // ── TTLock smart lock state ────────────────────────────────────────────────
  const [ttlockLocks, setTtlockLocks] = useState<TtlockLock[]>([]);
  const [ttlockError, setTtlockError] = useState<string | null>(null);
  const [ttlockClearingPins, setTtlockClearingPins] = useState(false);
  const [ttlockPinsClearedMsg, setTtlockPinsClearedMsg] = useState<string | null>(null);
  const [ttlockGeneratingPins, setTtlockGeneratingPins] = useState(false);
  const [ttlockPinsGeneratedMsg, setTtlockPinsGeneratedMsg] = useState<string | null>(null);
  const ttlockClearingRemaining = useBusyCountdown(ttlockClearingPins);
  const ttlockGeneratingRemaining = useBusyCountdown(ttlockGeneratingPins);

  // ── Dorucenka bulk-download state ──────────────────────────────────────────
  const [downloadJob, setDownloadJob] = useState<DorucenkaDownloadJob | null>(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>(monthAgo);
  const [dateTo, setDateTo] = useState<string>(today);
  const [requesting, setRequesting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Export state ───────────────────────────────────────────────────────────
  const [exportFrom, setExportFrom] = useState<string>(monthAgo);
  const [exportTo, setExportTo] = useState<string>(today);
  const [exportLoadingType, setExportLoadingType] = useState<'csv' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // ── Grouped navigation, unsaved changes, setup checklist (Issue #137) ──────────────────────
  // Ten tabs became five groups of the SAME sections: every section block below is still its own
  // `isShown('x') && sec('x', ...)` block, only the condition changed. `?tab=` accepts group keys
  // and every old tab key; `booking` (the unreleased direct-booking web) stays URL-only.
  const nav = apartmentEditNavText[lang];
  const loc = useLocation();
  const initialLocation = useRef(resolveLocation(searchParams.get('tab'), loc.hash));
  const [view, setView] = useState<ViewKey>(initialLocation.current.group);
  const viewRef = useRef<ViewKey>(view);
  viewRef.current = view;
  const pendingJump = useRef<JumpRequest | null>(scrollRequestFor(initialLocation.current));
  const firstUrlSync = useRef(true);
  const isShown = (section: SectionKey) => view === SECTION_GROUP[section];

  // Picking a group pushes a history entry (no `replace`) so Back steps through the groups.
  const selectGroup = (g: GroupKey) => {
    if (viewRef.current === g) return;
    viewRef.current = g;
    setView(g);
    setSearchParams(p => { p.set('tab', g); p.delete('find'); return p; });
  };
  const runJump = (req: JumpRequest) => {
    jumpTo(req, { showGroup: selectGroup, currentGroup: () => viewRef.current });
  };
  /** Old entry point, kept by name: also used by the in-page "open Payments" link. */
  const switchTab = (tab: TabKey) => {
    const r = resolveTabParam(tab);
    if (r.group === BOOKING_VIEW) {
      viewRef.current = BOOKING_VIEW;
      setView(BOOKING_VIEW);
      setSearchParams(p => { p.set('tab', 'booking'); return p; });
    } else if (r.section) {
      runJump({ group: r.group, section: r.section });
    } else {
      selectGroup(r.group);
    }
  };

  // Back/forward (and links typed into the bar) change `searchParams` without going through
  // selectGroup, so this keeps the visible group in step with the URL either way.
  useEffect(() => {
    if (firstUrlSync.current) { firstUrlSync.current = false; return; }
    const target = resolveLocation(searchParams.get('tab'), loc.hash);
    if (target.group !== viewRef.current) {
      viewRef.current = target.group;
      setView(target.group);
    }
    const req = scrollRequestFor(target);
    if (req) runJump(req);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // What the shared "Uložit" would send vs what the server holds. `savedForm` is a snapshot taken
  // right after every load(), which is why load() bumps `loadTick`.
  const [savedForm, setSavedForm] = useState<Apartment | null>(null);
  const [loadTick, setLoadTick] = useState(0);
  useEffect(() => {
    if (loadTick > 0) setSavedForm(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTick]);
  // The Ubyport cap exactly as the server holds it. The form shows a cap at or above the capacity as
  // "send all", so a save that left the cap alone sends this instead (withStoredMaxGuests).
  const [storedMaxGuests, setStoredMaxGuests] = useState<number | null>(null);
  // Switching auto-send on can bring back a saved cap or delay; the fold they sit in only mounts then,
  // so it is opened right after that render to keep them from being a surprise.
  const [revealAutoSendAdvanced, setRevealAutoSendAdvanced] = useState(false);
  useEffect(() => {
    if (!revealAutoSendAdvanced) return;
    openAutoSendAdvanced();
    setRevealAutoSendAdvanced(false);
  }, [revealAutoSendAdvanced]);
  const dirtyList = useMemo(() => dirtySections(form, savedForm), [form, savedForm]);
  const isDirty = dirtyList.length > 0;
  // Latch for this visit: after the first edit the controls stay put, even after save/discard.
  const [saveBarRevealed, setSaveBarRevealed] = useState(false);
  useEffect(() => { if (isDirty) setSaveBarRevealed(true); }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const discardChanges = () => {
    // The guide's save is still sending this form: reverting it now would let the next shared save
    // write the old values over what the server has just accepted.
    if (!savedForm || saving || guideSaving) return;
    setForm(savedForm);
    if (savedGuide) setGuide(current => ({ ...savedGuide, photos: current.photos }));
    if (savedForm.assignedContact) {
      setContactSource('list');
      setContactEditForm(contacts.find(c => c.id === savedForm.assignedContact?.id) || null);
    } else {
      setContactSource(savedForm.contactFullName || savedForm.contactEmail || savedForm.contactPhone ? 'other' : 'manager');
      setContactEditForm(null);
    }
    setError(null);
    setSuccess(null);
  };

  // The checklist reads SAVED state only: the apartment as loaded, the guide as loaded/saved, and
  // the deposit / stay-extension settings (which autosave elsewhere and are re-read on leaving Peníze).
  const facts = useChecklistFacts(apartmentId, getToken);
  const [savedGuide, setSavedGuide] = useState<CheckinGuideConfig | null>(null);
  // Photos persist immediately through their own API; only draft guide fields belong to Save/Discard.
  const guideDirty = savedGuide !== null && JSON.stringify({ ...guide, photos: [] }) !== JSON.stringify({ ...savedGuide, photos: [] });
  useEffect(() => { if (guideDirty) setSaveBarRevealed(true); }, [guideDirty]);
  useEffect(() => {
    if (!guideDirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [guideDirty]);
  const checklistItems = useMemo(() => deriveChecklist({
    saved: savedForm
      ? { ...savedForm, calendarSyncOk: facts.calendarSyncOk !== undefined ? facts.calendarSyncOk : savedForm.calendarSyncOk }
      : null,
    guide: savedGuide,
    deposit: facts.deposit,
    extensions: facts.extensions,
    paymentMethods,
  }), [savedForm, savedGuide, facts.calendarSyncOk, facts.deposit, facts.extensions, paymentMethods]);
  const checklistSummary = summarizeChecklist(checklistItems);

  const previousView = useRef(view);
  useEffect(() => {
    const prev = previousView.current;
    previousView.current = view;
    if (prev === 'money' && view !== 'money') facts.refresh({ money: true });
    if (prev === 'reservations' && view !== 'reservations') facts.refresh({ calendar: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const load = async () => {
    if (!apartmentId) return;
    setLoading(true);
    setError(null);
    try {
      const [apt, contactList, mailboxList, pmList, feeSettings] = await Promise.all([
        getApartmentById(apartmentId, getToken()),
        getMyContacts(getToken()).catch(() => [] as Contact[]),
        getCityTaxMailboxes(),
        listPaymentMethods().catch(() => [] as PaymentMethod[]),
        getPlatformFee(getToken()).catch(() => null),
      ]);
      setContacts(contactList);
      setMailboxes(mailboxList);
      setPaymentMethods(pmList);
      setPlatformFee(feeSettings);
      setLoadTick(n => n + 1);
      setStoredMaxGuests(apt.ubyportAutoSendMaxGuests ?? null);
      setForm({
        ...apt,
        autoSendToUbyport: apt.autoSendToUbyport || false,
        // Empty cap = every guest; no delay = the check-in day. Both are what a null on the server means.
        ubyportAutoSendMaxGuests: loadedMaxGuests(apt.ubyportAutoSendMaxGuests, apt.capacity),
        ubyportAutoSendDelayDays: apt.ubyportAutoSendDelayDays ?? 0,
        ubyportPassword: apt.hasUbyportPassword ? '••••••••' : '',
        cityTaxAdult: apt.cityTaxAdult ?? 50,
        cityTaxChild: apt.cityTaxChild ?? 0,
        cityTaxDisabled: apt.cityTaxDisabled ?? 0,
        cityTaxContactId: apt.cityTaxContactId ?? undefined,
        cityTaxContact: apt.cityTaxContactId ? { id: apt.cityTaxContactId } : undefined,
        // -1 sentinel = "Other" (no FK, but manual fields filled)
        cityTaxMailboxId: apt.cityTaxMailboxId
          ? apt.cityTaxMailboxId
          : (apt.cityTaxMailboxOtherName || apt.cityTaxMailboxOtherDataboxId || apt.cityTaxMailboxOtherAddress)
            ? -1
            : undefined,
        cityTaxMailbox: apt.cityTaxMailboxId ? { id: apt.cityTaxMailboxId } : undefined,
        cityTaxMailboxOtherName: apt.cityTaxMailboxOtherName,
        cityTaxMailboxOtherDataboxId: apt.cityTaxMailboxOtherDataboxId,
        cityTaxMailboxOtherAddress: apt.cityTaxMailboxOtherAddress,
        cityTaxCollectionEnabled: apt.cityTaxCollectionEnabled ?? false,
        // The effective account per purpose: an apartment that was never split reports its one
        // shared account for all three, so the pickers preselect correctly either way.
        paymentMethodId: apt.cityTaxPaymentMethodId ?? apt.paymentMethodId ?? undefined,
        paymentMethod: (apt.cityTaxPaymentMethodId ?? apt.paymentMethodId)
          ? { id: apt.cityTaxPaymentMethodId ?? apt.paymentMethodId }
          : undefined,
        upsellPaymentMethodId: apt.upsellPaymentMethodId ?? undefined,
        depositPaymentMethodId: apt.depositPaymentMethodId ?? undefined,
      });
      if (apt.assignedContact) {
        setContactSource('list');
        const found = contactList.find(c => c.id === apt.assignedContact?.id) || null;
        setContactEditForm(found);
      } else if (apt.contactFullName || apt.contactEmail || apt.contactPhone) {
        setContactSource('other');
      } else {
        setContactSource('manager');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load apartment detail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    fetchCurrentJob();
  }, [apartmentId]);

  // An old `?tab=citytax` / `#dorucenka-download` link scrolls to its section once the page exists.
  useEffect(() => {
    if (loading || !form || !pendingJump.current) return;
    const req = pendingJump.current;
    pendingJump.current = null;
    runJump(req);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ── Dorucenka job: fetch + polling ─────────────────────────────────────────

  const fetchCurrentJob = async () => {
    if (!apartmentId) return;
    setJobLoading(true);
    try {
      const job = await getCurrentDorucenkaDownloadJob(apartmentId);
      setDownloadJob(job);
    } catch {
      // non-critical — don't surface to main error state
    } finally {
      setJobLoading(false);
    }
  };

  // Poll every 10 s while a job is in-flight; stop when READY/FAILED/EXPIRED
  useEffect(() => {
    // Loaded on page load (not only on the guide tab) because the setup checklist needs it.
    if (!guideLoaded && apartmentId) {
      getCheckinGuideConfig(apartmentId, getToken())
        .then(data => {
          const loaded = { ...emptyGuideConfig(), ...data, otherLinks: data.otherLinks ?? [], photos: data.photos ?? [] };
          setGuide(loaded);
          setSavedGuide(loaded);
          setGuideLoaded(true);
        })
        .catch(() => { setGuideLoaded(true); });
    }
  }, [guideLoaded, apartmentId]);

  const saveGuide = async () => {
    if (!form || !apartmentId || saving || guideSaving || !guideLoaded) return;
    if (autoSendErrors(form).length) {
      openAutoSendAdvanced();
      setGuideError(t.autoSendInvalidSave);
      return;
    }
    if (form.cityTaxCollectionEnabled) {
      const selected = paymentMethods.find(pm => pm.type === 'STRIPE' && pm.id === form.paymentMethodId);
      if (!selected || !selected.stripeChargesEnabled) {
        setGuideError(t.cityTaxPaymentMethodRequiredError);
        return;
      }
    }
    const sentForm = form;
    const sentGuide = guide;
    setError(null); setSuccess(null);
    setGuideSaving(true); setGuideError(null); setGuideSuccess(null);
    try {
      // An untouched sample is a starting point, not the host's instruction: it is never persisted,
      // so it can never reach a guest as one (#131).
      const saved = await saveCheckinGuideConfig(apartmentId, withoutUneditedSamples(sentGuide), getToken());
      const acknowledgedGuide = { ...emptyGuideConfig(), ...saved, otherLinks: saved.otherLinks ?? [], photos: saved.photos ?? [] };
      setGuide(current => current === sentGuide ? acknowledgedGuide : current);
      setSavedGuide(acknowledgedGuide);
      const payload: Apartment = withStoredMaxGuests(
        withSendableAutoSend(withPaymentMethodPayload({ ...sentForm }), savedForm), savedForm, storedMaxGuests);
      if (!payload.ubyportPassword || !payload.ubyportPassword.trim() || payload.ubyportPassword === '••••••••') delete payload.ubyportPassword;
      await updateApartment(apartmentId, payload, getToken());
      setStoredMaxGuests(payload.ubyportAutoSendMaxGuests ?? null);
      const typed = !!payload.ubyportPassword;
      setSavedForm(typed ? { ...sentForm, hasUbyportPassword: true } : sentForm);
      setGuideSuccess(t.guideSavedSuccess);
    } catch (e: any) {
      setGuideError(saveErrorMessage(e, 'Failed to save guide'));
    } finally { setGuideSaving(false); }
  };

  useEffect(() => {
    if (view === 'arrival') {
      getTtlockLocks(getToken()).then(setTtlockLocks).catch(() => {});
    }
  }, [view]);

  useEffect(() => {
    const shouldPoll = downloadJob && ACTIVE_JOB_STATUSES.includes(downloadJob.status);
    if (shouldPoll) {
      pollRef.current = setInterval(fetchCurrentJob, 10_000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [downloadJob?.status]);

  const handleRequestDownload = async () => {
    if (!dateFrom || !dateTo) return;
    setRequesting(true);
    setJobError(null);
    try {
      const job = await requestDorucenkaDownload(apartmentId, dateFrom, dateTo);
      setDownloadJob(job);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to request download';
      setJobError(msg);
    } finally {
      setRequesting(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!downloadJob) return;
    setDownloading(true);
    try {
      await downloadDorucenkaZip(apartmentId, downloadJob.id);
    } catch (err: any) {
      setJobError(err.response?.data?.message || err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // "Test Ubyport": the answer belongs to the values that were tested, so editing either field
  // discards it rather than leave a green tick beside a password nobody checked.
  const [ubyportTest, setUbyportTest] = useState<
    { state: 'idle' } | { state: 'pending' } | { state: 'done'; result: UbyportCredentialCheck } | { state: 'failed' }
  >({ state: 'idle' });
  const ubyportTestInFlight = useRef(false);
  const ubyportTestRun = useRef(0);

  // Fields the last click found empty; that click sent nothing, so there is no server answer to show.
  const [ubyportMissing, setUbyportMissing] = useState<Array<'ubyportUsername' | 'ubyportPassword' | 'idub'>>([]);

  const runUbyportTest = async () => {
    if (!form || !apartmentId || ubyportTestInFlight.current) return;
    // A stored password counts as filled: the box then holds only the mask or nothing.
    // IDUB is only checked for being filled: the Ubyport call in the backend does not use it, but a
    // manager who left it empty has not finished setting Ubyport up.
    const missing: Array<'ubyportUsername' | 'ubyportPassword' | 'idub'> = [];
    if (!form.ubyportUsername?.trim()) missing.push('ubyportUsername');
    if (!form.ubyportPassword?.trim() && !form.hasUbyportPassword) missing.push('ubyportPassword');
    if (!form.idub?.trim()) missing.push('idub');
    if (missing.length) {
      ubyportTestRun.current++;
      setUbyportTest({ state: 'idle' });
      setUbyportMissing(missing);
      const ids = { ubyportUsername: 'ae-ubyportUsername', ubyportPassword: 'ae-ubyportPassword', idub: 'ae-idub' };
      document.getElementById(ids[missing[0]])?.focus();
      return;
    }
    setUbyportMissing([]);
    ubyportTestInFlight.current = true;
    const run = ++ubyportTestRun.current;
    setUbyportTest({ state: 'pending' });
    // The stored password never reaches the browser, so the mask (or an empty box) means "use the stored one".
    const typedPassword = form.ubyportPassword && form.ubyportPassword.trim() && form.ubyportPassword !== '••••••••'
      ? form.ubyportPassword : undefined;
    try {
      const result = await testUbyportCredentials(apartmentId, {
        username: form.ubyportUsername?.trim() || undefined,
        password: typedPassword,
      }, getToken());
      if (run === ubyportTestRun.current) setUbyportTest({ state: 'done', result });
    } catch {
      if (run === ubyportTestRun.current) setUbyportTest({ state: 'failed' });
    } finally {
      ubyportTestInFlight.current = false;
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!form) return;
    if (e.target.name === 'ubyportUsername' || e.target.name === 'ubyportPassword' || e.target.name === 'idub') {
      ubyportTestRun.current++;
      setUbyportTest({ state: 'idle' });
      const edited = e.target.name;
      setUbyportMissing(m => m.filter(x => x !== edited));
    }
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm({ ...form, [name]: checked });
      return;
    }
    if (name === 'contactId') {
      const cid = parseInt(value || '0', 10) || 0;
      setForm({ ...form, assignedContact: cid > 0 ? { id: cid, name: '' } : undefined });
      const found = contacts.find(c => c.id === cid) || null;
      setContactEditForm(found);
      return;
    }
    if (name === 'cityTaxContactId') {
      const cid = parseInt(value || '0', 10) || 0;
      setForm({ ...form, cityTaxContactId: cid > 0 ? cid : undefined, cityTaxContact: cid > 0 ? { id: cid } : undefined });
      return;
    }
    if (name === 'cityTaxMailboxId') {
      const mid = parseInt(value || '0', 10) || 0;
      // -1 = "Other" sentinel; >0 = predefined; 0 = none
      setForm({ ...form, cityTaxMailboxId: mid !== 0 ? mid : undefined, cityTaxMailbox: mid > 0 ? { id: mid } : undefined });
      return;
    }
    if (name === 'paymentMethodId') {
      const pmid = parseInt(value || '0', 10) || 0;
      setForm({ ...form, paymentMethodId: pmid > 0 ? pmid : undefined, paymentMethod: pmid > 0 ? { id: pmid } : undefined });
      return;
    }
    if (name === 'upsellPaymentMethodId' || name === 'depositPaymentMethodId') {
      const pmid = parseInt(value || '0', 10) || 0;
      setForm({ ...form, [name]: pmid > 0 ? pmid : undefined });
      return;
    }
    if (name === 'capacity') {
      setForm({ ...form, capacity: parseInt(value || '1', 10) || 1 });
      return;
    }
    if (name === 'publicCheckinWindowDays') {
      const parsed = parseInt(value, 10);
      setForm({ ...form, publicCheckinWindowDays: isNaN(parsed) ? undefined : Math.max(1, Math.min(30, parsed)) });
      return;
    }
    if (name === 'cityTaxAdult' || name === 'cityTaxChild' || name === 'cityTaxDisabled') {
      setForm({ ...form, [name]: value === '' ? undefined : parseFloat(value) });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const saveApartment = async () => {
    if (!form || !apartmentId || saving || guideSaving) return;

    if (autoSendErrors(form).length) {
      openAutoSendAdvanced();
      setError(t.autoSendInvalidSave);
      return;
    }

    if (form.cityTaxCollectionEnabled) {
      const selected = paymentMethods.find(pm => pm.type === 'STRIPE' && pm.id === form.paymentMethodId);
      if (!selected || !selected.stripeChargesEnabled) {
        setError(t.cityTaxPaymentMethodRequiredError);
        return;
      }
    }

    setSaving(true);
    setGuideError(null); setGuideSuccess(null);
    setError(null);
    setSuccess(null);
    try {
      // All three account pickers are always sent, an empty one as id 0 — see withPaymentMethodPayload.
      const payload: Apartment = withStoredMaxGuests(
        withSendableAutoSend(withPaymentMethodPayload({ ...form }), savedForm), savedForm, storedMaxGuests);

      // Blank or sentinel means "keep existing stored secret".
      if (!payload.ubyportPassword || !payload.ubyportPassword.trim() || payload.ubyportPassword === '••••••••') {
        delete payload.ubyportPassword;
      }

      const saved = await updateApartment(apartmentId, payload, getToken());
      setStoredMaxGuests(payload.ubyportAutoSendMaxGuests ?? null);
      setSuccess(t.savedSuccess);
      if (saved.ttlockPinsClearedCount && saved.ttlockPinsClearedCount > 0) {
        setTtlockPinsClearedMsg(
          `Lock updated — ${saved.ttlockPinsClearedCount} upcoming reservation PIN(s) cleared and will be re-generated automatically.`
        );
      }
      // Acknowledge only the submitted snapshot. Keep the page mounted (and preserve newer edits).
      const acknowledged: Apartment = {
        ...form, ...saved,
        ubyportPassword: (saved.hasUbyportPassword || form.hasUbyportPassword || payload.ubyportPassword) ? '••••••••' : '',
        // The server echoes the stored cap, which can be one the form shows as "send all" (see load()).
        ubyportAutoSendMaxGuests: isValidMaxGuests(form.ubyportAutoSendMaxGuests)
          ? form.ubyportAutoSendMaxGuests
          : loadedMaxGuests(payload.ubyportAutoSendMaxGuests, form.capacity),
        // Match load(): manual mailbox details have no FK, but the form uses -1 for "Other".
        cityTaxMailboxId: saved.cityTaxMailboxId ||
          ((saved.cityTaxMailboxOtherName || saved.cityTaxMailboxOtherDataboxId || saved.cityTaxMailboxOtherAddress) ? -1 : saved.cityTaxMailboxId ?? form.cityTaxMailboxId),
      };
      setSavedForm(acknowledged);
      setForm(current => {
        if (!current || current === form) return acknowledged;
        const newerEdits = Object.fromEntries(Object.entries(current).filter(([key, value]) =>
          JSON.stringify(value) !== JSON.stringify(form[key as keyof Apartment])));
        return { ...acknowledged, ...newerEdits };
      });
    } catch (err: any) {
      setError(saveErrorMessage(err, 'Failed to save apartment settings'));
    } finally {
      setSaving(false);
    }
  };

  const purposeLabel = (purpose: PaymentPurpose) =>
    purpose === 'CITY_TAX' ? t.paymentsPurposeCityTax
      : purpose === 'UPSELL' ? t.paymentsPurposeUpsell
        : t.paymentsPurposeDeposit;

  // A refused account is the one save failure with a specific thing for the manager to do, so it
  // gets its own sentence instead of the generic fallback.
  const saveErrorMessage = (err: any, fallback: string): string => {
    const refused = invalidPaymentMethodPurpose(err);
    if (refused) return t.paymentMethodInvalidError.replace('{purpose}', purposeLabel(refused));
    return err.response?.data?.message || err.message || fallback;
  };


  const handleDeleteApartment = async () => {
    if (!apartmentId) return;
    setError(null);
    try {
      const summary = await getApartmentDeleteSummary(apartmentId, getToken());
      setDeleteSummary(summary);
      setDeleteConfirmName('');
      setDeleteModalOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load delete summary');
    }
  };

  const handleConfirmDelete = async () => {
    if (!apartmentId) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteApartment(apartmentId, getToken());
      navigate(returnTo);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete apartment');
      setDeleteModalOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const publicCheckinUrl = form?.publicCheckinKey
    ? `${window.location.origin}/check-in/${form.publicCheckinKey}`
    : '';

  const handleCopyPublicCheckinUrl = async () => {
    if (!publicCheckinUrl) return;
    try {
      await navigator.clipboard.writeText(publicCheckinUrl);
      setCopySuccess(t.copySuccess);
      window.setTimeout(() => setCopySuccess(null), 2000);
    } catch {
      setError(t.copyError);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1100, margin: '30px auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 24 }}>
        <SectionLoader label={t.loading} minHeight={280} />
      </div>
    );
  }

  if (!form) {
    return <div style={{ maxWidth: 1000, margin: '30px auto', color: 'red' }}>{t.notFound}</div>;
  }

  // Empty when auto-send is off: its controls are not shown, so an error there could not be fixed.
  const autoSendFieldErrors = autoSendErrors(form);

  /**
   * A section is either a plain headed block or — with `cx` — a card closed behind one line: the
   * title, what is set today, and a status pill. The heading stays an `<h3>` in both, so the page's
   * outline (and the tests that read it) do not change with the presentation.
   */
  const sec = (
    key: SectionKey, title: string, node: React.ReactNode, busy = false,
    cx?: { summary: React.ReactNode; chip?: { tone: 'ok' | 'off' | 'warn' | 'bad'; text: string } },
  ) => {
    const domId = sectionDomId(key);
    if (!cx) {
      return (
        <section id={domId} className="ae-section" tabIndex={-1} aria-labelledby={`${domId}-h`} aria-busy={busy || undefined}>
          <h3 id={`${domId}-h`} className="ae-section-h">{stripLeadingSymbols(title)}</h3>
          {node}
        </section>
      );
    }
    return (
      <section id={domId} className="ae-section ae-cx" tabIndex={-1} aria-labelledby={`${domId}-h`} aria-busy={busy || undefined}>
        <details className="ae-cx-details">
          <summary className="ae-cx-summary">
            <div className="ae-cx-text">
              <h3 id={`${domId}-h`} className="ae-cx-title">{stripLeadingSymbols(title)}</h3>
              <small className="ae-cx-sum">{cx.summary}</small>
            </div>
            <span className="ae-cx-aside">
              {dirtyList.includes(key) && <span className="ae-cx-unsaved" role="img" aria-label={nav.cardUnsaved} title={nav.cardUnsaved} />}
              {cx.chip && <span className={`ae-pill ae-pill-${cx.chip.tone}`}>{cx.chip.text}</span>}
              <span className="ae-cx-chevron" aria-hidden="true">⌄</span>
            </span>
          </summary>
          <div className="ae-cx-body">{node}</div>
        </details>
      </section>
    );
  };

  // ── Guide, Variant 4: five groups, each with a one-line summary of what is set ─────────────────
  const guideSwitch = (key: keyof CheckinGuideConfig, label: string) => (
    <label className="ae-switchrow" htmlFor={`ae-guide-${key}`}>
      <span>{stripLeadingSymbols(label)}</span>
      <input type="checkbox" id={`ae-guide-${key}`} checked={!!guide[key]}
        onChange={e => setGuide(g => ({ ...g, [key]: e.target.checked }))} />
    </label>
  );
  const guideGroup = (id: string, icon: string, title: string, summary: string, body: React.ReactNode) => (
    // `name` makes the browser close the other groups when one opens (an exclusive accordion).
    <details key={id} className="ae-g" id={`ae-guide-group-${id}`} name="ae-guide-groups">
      <summary className="ae-g-summary">
        <span className="ae-g-ico" aria-hidden="true">{icon}</span>
        <span className="ae-g-text">
          <span className="ae-g-title">{title}</span>
          <span className="ae-g-sum">{summary}</span>
        </span>
        <span className="ae-cx-chevron" aria-hidden="true">⌄</span>
      </summary>
      <div className="ae-g-body">{body}</div>
    </details>
  );
  const accessTypeLabel: Record<string, string> = {
    DOOR_CODE: t.guideAccessTypeDoorCode, KEYBOX: t.guideAccessTypeKeybox, PERSON: t.guideAccessTypePerson,
    NONE: t.guideAccessTypeNoKey, TTLOCK: stripLeadingSymbols(t.guideAccessTypeTtlock),
  };
  const guideAccessSummary = (() => {
    if (!guide.showKeyAccess) return nav.gsHidden;
    const type = guide.keyAccessType || '';
    const parts = [accessTypeLabel[type] || stripLeadingSymbols(t.guideAccessTypeSelect)];
    if ((type === 'DOOR_CODE' || type === 'KEYBOX') && !guide.keyCode?.trim()) parts.push(nav.gsCodeMissing);
    if (type === 'TTLOCK' && !form?.ttlockLockId) parts.push(nav.gsLockMissing);
    return parts.join(' · ');
  })();
  const guideStaySummary = [
    guide.showWifi ? (guide.wifiName ? stripLeadingSymbols(t.guideSecWifi) : nav.gsWifiMissing) : null,
    guide.showParking ? stripLeadingSymbols(t.guideSecParking) : null,
    guide.showContact ? stripLeadingSymbols(t.guideSecHostContact) : null,
    guide.showWeather ? stripLeadingSymbols(t.guideSecWeather) : null,
  ].filter(Boolean).join(' · ') || nav.gsNothingShown;
  const guideContentSummary = [
    guide.showDetailedGuide && guide.detailedGuide ? stripLeadingSymbols(t.guideSecDetailedGuide) : null,
    guide.showAttentionNotes && guide.attentionNotes ? stripLeadingSymbols(t.guideSecAttentionNotes) : null,
    guide.showPhotos && guide.photos.length ? `${stripLeadingSymbols(t.guideSecPhotos)} (${guide.photos.length})` : null,
    guide.showOtherLinks && guide.otherLinks.length ? `${stripLeadingSymbols(t.guideSecOtherLinks)} (${guide.otherLinks.length})` : null,
  ].filter(Boolean).join(' · ') || nav.gsOptional;
  const guideDepartureSummary = guide.showCheckoutInstructions
    ? nav.gsDeparture(guide.checkOutTime || '', !!guide.checkoutInstructions?.trim())
    : nav.gsHidden;

  const dirtyGroupSet = new Set(dirtyList.map(sectionKey => SECTION_GROUP[sectionKey]));
  if (guideDirty) dirtyGroupSet.add('arrival');
  const attentionGroups = groupsNeedingAttention(checklistItems);
  const badges = {} as Record<GroupKey, GroupBadge>;
  GROUP_ORDER.forEach(g => { badges[g] = { attention: attentionGroups.has(g), unsaved: dirtyGroupSet.has(g) }; });
  const unsavedGroupNames = GROUP_ORDER.filter(g => dirtyGroupSet.has(g)).map(g => groupLabel(nav, g));
  const subtitle = [form.internalName, form.address].filter(Boolean).join(' · ') || t.subtitle;

  return (
    <div className="ae-page" style={{ maxWidth: 1100, margin: '30px auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 24 }}>
      <Link
        to={returnTo}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600, textDecoration: 'none', marginBottom: 12, fontSize: '0.9rem' }}
      >
        {t.back}
      </Link>
      <header className="ae-header">
        <h2>{form.name}</h2>
        <p className="ae-subtitle">{subtitle}</p>
      </header>

      {(error || guideError || success || guideSuccess) && (
        <div className={`ae-notice${error || guideError ? ' ae-notice-error' : ''}`} role={error || guideError ? 'alert' : 'status'}>
          {error || guideError || success || guideSuccess}
        </div>
      )}

      <SetupChecklist items={checklistItems} summary={checklistSummary} text={nav} onJump={item => runJump(item.jump)} />
      <SettingsSearch
        lang={lang} detailText={t} text={nav} initialQuery={searchParams.get('find') || ''}
        onPick={entry => runJump({ group: entry.group, section: entry.section, targetId: entry.targetId, fallbackId: entry.fallbackId })}
      />
      <GroupTabs active={view} badges={badges} text={nav} onSelect={selectGroup} />

      <div id={TAB_PANEL_ID} role={view === BOOKING_VIEW ? undefined : 'tabpanel'} aria-labelledby={view === BOOKING_VIEW ? undefined : tabDomId(view)}>
      {isShown('general') && sec('general', t.tabGeneral, (
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">{t.fieldInternalName}</label>
            <input id="ae-internalName" className="form-control" name="internalName" maxLength={100} value={form.internalName || ''} onChange={onChange} />
          </div>
          <div className="form-field">
            <label className="form-label">{t.fieldDisplayName}</label>
            <input id="ae-displayName" className="form-control" name="displayName" maxLength={100} value={form.displayName || ''} onChange={onChange} />
          </div>
          <div className="form-field">
            <label className="form-label">{t.fieldAddress}</label>
            <input id="ae-address" className="form-control" name="address" value={form.address} onChange={onChange} />
          </div>
          <div className="form-field">
            <label className="form-label">{t.fieldCapacity}</label>
            <input id="ae-capacity" className="form-control" type="number" min={1} name="capacity" value={form.capacity} onChange={onChange} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="form-label"><input id="ae-isActive" type="checkbox" name="isActive" checked={form.isActive} onChange={onChange} /> {t.fieldActive}</label>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <AdvancedDetails title={nav.advanced} count={1}>
              <div className="form-field">
                <label className="form-label">{t.fieldDescription}</label>
                <textarea id="ae-description" className="form-control" name="description" value={form.description} onChange={onChange} style={{ minHeight: 80 }} />
              </div>
            </AdvancedDetails>
          </div>
          <div id="ae-automation" style={{ gridColumn: '1 / -1', marginTop: 8, background: '#f7fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, color: '#2d3748' }}>{t.sectionAutomation}</div>
              <div style={{ fontSize: '0.8rem', color: '#4a5568', background: '#e2e8f0', padding: '2px 8px', borderRadius: 999 }}>{t.badgePublicCheckin}</div>
            </div>
            <div className="ae-checkin-card">
              <div className="ae-checkin-head"><b>{nav.checkinLinkTitle}</b></div>
              <div className="form-field">
                <label className="form-label" htmlFor="ae-checkinUrl">{t.fieldCheckinUrl}</label>
                <div className="ae-copy-row">
                  <input id="ae-checkinUrl" className="form-control ae-checkin-url" value={publicCheckinUrl}
                    readOnly onFocus={e => e.currentTarget.select()} />
                  <button type="button" className="ae-btn ae-btn-primary" onClick={handleCopyPublicCheckinUrl} disabled={!publicCheckinUrl}>
                    {t.btnCopy}
                  </button>
                </div>
                <div className="ae-hint">{t.checkinUrlHint}</div>
                {copySuccess && <div role="status" style={{ color: '#22543d', marginTop: 4 }}>{copySuccess}</div>}
              </div>
            <AdvancedDetails title={nav.advanced} count={2}>
            <div className="form-field" style={{ maxWidth: 280 }}>
              <label className="form-label">{t.fieldCheckinWindowDays}</label>
              <input
                id="ae-publicCheckinWindowDays"
                className="form-control"
                type="number"
                min={1}
                max={30}
                name="publicCheckinWindowDays"
                value={form.publicCheckinWindowDays ?? ''}
                onChange={onChange}
              />
              <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>
                {t.checkinWindowHint(form.publicCheckinWindowDays || 10)}
              </div>
            </div>

            {/* ── Document scan (guest-side MRZ prefill) ──────────────────── */}
            <div id="ae-documentScan" style={{
              gridColumn: '1 / -1', marginTop: 16, padding: 16, borderRadius: 10, transition: 'all 0.15s',
              border: `2px solid ${form.documentScanEnabled ? '#48bb78' : '#e2e8f0'}`,
              background: form.documentScanEnabled ? '#f0fff4' : '#f8fafc',
            }}>
              <div
                onClick={() => setForm(f => f ? { ...f, documentScanEnabled: !f.documentScanEnabled } : f)}
                style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
              >
                <div style={{
                  width: 44, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
                  background: form.documentScanEnabled ? '#48bb78' : '#cbd5e0', transition: 'background 0.15s',
                }}>
                  <div style={{
                    position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                    left: form.documentScanEnabled ? 23 : 3,
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: form.documentScanEnabled ? '#276749' : '#2d3748' }}>
                    {t.documentScanLabel}
                  </div>
                  <div style={{ fontSize: '0.82rem', marginTop: 2, color: form.documentScanEnabled ? '#2f855a' : '#718096' }}>
                    {t.documentScanHint}
                  </div>
                </div>
              </div>
            </div>
            </AdvancedDetails>
            </div>

            <HelpDetails title={t.automationInfoTitle}>
            <div style={{ padding: '16px 18px', background: '#f0f7ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap', rowGap: 12 }}>
                {/* Step 1 */}
                <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>🔗</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.automationStep1Title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.automationStep1Desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0 }}>›</div>
                {/* Step 2 */}
                <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>✉️</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.automationStep2Title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.automationStep2Desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0 }}>›</div>
                {/* Step 3 */}
                <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>📱</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.automationStep3Title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.automationStep3Desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0 }}>›</div>
                {/* Step 4 */}
                <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dbeafe', border: '2px solid #93c5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>📝</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e3a8a', marginBottom: 4 }}>{t.automationStep4Title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#3b82f6', lineHeight: 1.4 }}>{t.automationStep4Desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#93c5fd', fontSize: '1.2rem', flexShrink: 0 }}>›</div>
                {/* Step 5 */}
                <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 110 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dcfce7', border: '2px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#166534', marginBottom: 4 }}>{t.automationStep5Title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#16a34a', lineHeight: 1.4 }}>{t.automationStep5Desc}</div>
                </div>
              </div>
              <div style={{ marginTop: 14, padding: '8px 12px', background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe', fontSize: '0.78rem', color: '#1d4ed8' }}>
                {t.automationTip}
              </div>
            </div>
            </HelpDetails>
          </div>
        </div>
      ))}

      {isShown('ubyport') && sec('ubyport', t.tabUbyport, (
        <div>
          <div style={{ marginBottom: 16, background: '#f7fafc', padding: 12, borderRadius: 8, color: '#2d3748' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.ubyportCredentialsHeader}</div>
            <div style={{ fontSize: '0.92rem', color: '#4a5568' }}>
              {t.ubyportCredentialsDesc}
              {' '}
              <a href="https://guestbook.cz/guide.html#ubyport" target="_blank" rel="noopener noreferrer" style={{ color: '#2b6cb0', textDecoration: 'underline' }}>
                {t.ubyportGuideLabel}
              </a>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">{t.fieldUbyportUsername}</label>
              <input id="ae-ubyportUsername" className={`form-control${ubyportMissing.includes('ubyportUsername') ? ' invalid' : ''}`} aria-invalid={ubyportMissing.includes('ubyportUsername')} name="ubyportUsername" value={form.ubyportUsername || ''} onChange={onChange} placeholder={t.ubyportUsernamePlaceholder} />
              {form.ubyportUsername && !form.ubyportUsername.startsWith('UBY-WS_') ? (
                <div style={{ fontSize: '0.85rem', color: '#c05621', background: '#fffaf0', border: '1px solid #f6ad55', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
                  ⚠ {t.ubyportUsernameWarning}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>{t.ubyportUsernameHint}</div>
              )}
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldUbyportPassword}</label>
              <input id="ae-ubyportPassword" className={`form-control${ubyportMissing.includes('ubyportPassword') ? ' invalid' : ''}`} aria-invalid={ubyportMissing.includes('ubyportPassword')} type="password" name="ubyportPassword" value={form.ubyportPassword || ''} onChange={onChange} placeholder={form.hasUbyportPassword ? t.ubyportPasswordPlaceholderSet : t.ubyportPasswordPlaceholderEmpty} onFocus={() => { if (form.ubyportPassword === '••••••••') setForm(f => f ? { ...f, ubyportPassword: '' } : f); }} />
              <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>
                {form.hasUbyportPassword ? t.ubyportPasswordHintSet : t.ubyportPasswordHintEmpty}
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldIdub}</label>
              <input id="ae-idub" className={`form-control${ubyportMissing.includes('idub') ? ' invalid' : ''}`} aria-invalid={ubyportMissing.includes('idub')} name="idub" maxLength={15} value={form.idub || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t.fieldShortName}
                <span
                  title={t.shortNameHint}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#bee3f8', color: '#2b6cb0', fontSize: '0.7rem', fontWeight: 700, cursor: 'help', flexShrink: 0 }}
                >
                  ?
                </span>
              </label>
              <input id="ae-ubyportShortName" className="form-control" name="ubyportShortName" maxLength={10} value={form.ubyportShortName || ''} onChange={onChange} placeholder={t.shortNamePlaceholder} />
              <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>{t.shortNameHint}</div>
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldHostFullName}</label>
              <input id="ae-hostFullName" className="form-control" name="hostFullName" value={form.hostFullName || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldHostContact}</label>
              <input id="ae-hostPhone" className="form-control" name="hostPhone" value={form.hostPhone || ''} onChange={onChange} placeholder={t.hostContactPlaceholder} />
              <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>{t.hostContactHint}</div>
            </div>
            <div className="form-field">
              <span className="form-label" aria-hidden="true">&nbsp;</span>
              <button
                type="button"
                id="ae-ubyportTest"
                className="app-btn app-btn-blue app-btn-standard ae-fieldheight-btn"
                onClick={runUbyportTest}
                disabled={ubyportTest.state === 'pending' || !apartmentId}
                aria-busy={ubyportTest.state === 'pending'}
              >
                {ubyportTest.state === 'pending' ? t.ubyportTestRunning : t.ubyportTestButton}
              </button>
              <div role="status" aria-live="polite" id="ae-ubyportTestResult">
                {(() => {
                  type Tone = 'ok' | 'bad' | 'warn';
                  let shown: { tone: Tone; text: string } | null = null;
                  if (ubyportMissing.length) {
                    const names = { ubyportUsername: t.ubyportTestFieldUsername, ubyportPassword: t.ubyportTestFieldPassword, idub: t.ubyportTestFieldIdub };
                    shown = { tone: 'bad', text: `${t.ubyportTestFillIn}: ${ubyportMissing.map(m => names[m]).join(', ')}` };
                  } else if (ubyportTest.state === 'done') {
                    const r = ubyportTest.result;
                    const message = ({
                      OK: t.ubyportTestOk,
                      INVALID_CREDENTIALS: t.ubyportTestInvalid,
                      INCOMPLETE: t.ubyportTestIncomplete,
                      TIMEOUT: t.ubyportTestTimeout,
                      UNREACHABLE: t.ubyportTestUnreachable,
                      PROVIDER_ERROR: t.ubyportTestProviderError,
                      NOT_CONFIGURED: t.ubyportTestNotConfigured,
                      BUSY: t.ubyportTestBusy,
                      RATE_LIMITED: t.ubyportTestRateLimited,
                    } as Record<string, string>)[r.status] || t.ubyportTestFailed;
                    const tone: Tone = r.ok ? 'ok' : r.status === 'INVALID_CREDENTIALS' || r.status === 'INCOMPLETE' ? 'bad' : 'warn';
                    shown = { tone, text: message };
                  } else if (ubyportTest.state === 'failed') {
                    shown = { tone: 'warn', text: t.ubyportTestFailed };
                  }
                  if (!shown) return null;
                  const color = { ok: '#276749', bad: '#c53030', warn: '#975a16' }[shown.tone];
                  const icon = { ok: '\u2713', bad: '\u2717', warn: '!' }[shown.tone];
                  return <div className="ae-ubyport-result" style={{ color }}>{icon} {shown.text}</div>;
                })()}
              </div>
            </div>
            {/* One card: the auto-send switch and, while it is on, its Advanced settings - they go together. */}
            <div id="ae-autoSendToUbyport" style={{
              gridColumn: '1 / -1', borderRadius: 10, transition: 'all 0.15s',
              border: `2px solid ${form.autoSendToUbyport ? '#48bb78' : '#e2e8f0'}`,
              background: form.autoSendToUbyport ? '#f0fff4' : '#f8fafc',
            }}>
              <div
                onClick={() => {
                  if (form.autoSendToUbyport) { setForm(f => f && { ...f, autoSendToUbyport: false }); return; }
                  const values = autoSendValuesOnSwitchOn(form);
                  if (!isAutoSendDefault(values)) setRevealAutoSendAdvanced(true);
                  setForm(f => f && { ...f, autoSendToUbyport: true, ...values });
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', cursor: 'pointer' }}
              >
                {/* Toggle pill */}
                <div style={{
                  width: 44, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
                  background: form.autoSendToUbyport ? '#48bb78' : '#cbd5e0', transition: 'background 0.15s',
                }}>
                  <div style={{
                    position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                    left: form.autoSendToUbyport ? 23 : 3,
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: form.autoSendToUbyport ? '#276749' : '#2d3748' }}>
                    {t.autoSendLabel} &nbsp;
                    <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                      {form.autoSendToUbyport ? t.autoSendEnabled : t.autoSendDisabled}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.82rem', marginTop: 2, color: form.autoSendToUbyport ? '#2f855a' : '#718096' }}>
                    {form.autoSendToUbyport ? t.autoSendHintEnabled : t.autoSendHintDisabled}
                  </div>
                </div>
              </div>
            {form.autoSendToUbyport && (() => {
              // "Send all guests" is the default and what nearly everyone wants, so the cap and the
              // delay sit in the page's usual closed "Advanced settings" disclosure.
              const limit = maxGuestsLimit(form.capacity);
              const sendAll = form.ubyportAutoSendMaxGuests === null || form.ubyportAutoSendMaxGuests === undefined;
              const maxInvalid = autoSendFieldErrors.includes('ubyportAutoSendMaxGuests');
              return (
                <div style={{ padding: '0 16px 12px' }}>
                  <AdvancedDetails id={AUTO_SEND_ADVANCED_ID} title={nav.advanced} count={2}>
                    <div id="ae-autoSendControls" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                      <div className="form-field">
                        <button
                          type="button"
                          id="ae-ubyportAutoSendAll"
                          role="switch"
                          aria-checked={sendAll}
                          onClick={() => setForm(f => f ? {
                            ...f,
                            // Off starts at the largest allowed cap (the capacity, at most 50) and the host lowers it.
                            ubyportAutoSendMaxGuests: sendAll ? maxGuestsLimit(f.capacity) : null,
                          } : f)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: 0,
                            background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: '#2d3748',
                          }}
                        >
                          <span aria-hidden="true" style={{
                            width: 36, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative',
                            background: sendAll ? '#48bb78' : '#cbd5e0', transition: 'background 0.15s',
                          }}>
                            <span style={{
                              position: 'absolute', top: 3, width: 14, height: 14, borderRadius: '50%',
                              background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                              left: sendAll ? 19 : 3,
                            }} />
                          </span>
                          <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{t.autoSendAllLabel}</span>
                        </button>
                        <label className="form-label" htmlFor="ae-ubyportAutoSendMaxGuests">{t.autoSendMaxLabel}</label>
                        <input
                          id="ae-ubyportAutoSendMaxGuests"
                          className={`form-control${maxInvalid ? ' invalid' : ''}`}
                          aria-invalid={maxInvalid}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="off"
                          name="ubyportAutoSendMaxGuests"
                          disabled={sendAll}
                          // A cap is one or two digits: a full-width box looked like a text field, and a
                          // disabled one must look disabled (form-control has no :disabled style).
                          style={{ maxWidth: 120, ...(sendAll ? { background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed' } : {}) }}
                          value={sendAll ? String(limit) : maxGuestsInputValue(form.ubyportAutoSendMaxGuests)}
                          onChange={e => {
                            const next = parseMaxGuestsInput(e.target.value);
                            setForm(f => f ? { ...f, ubyportAutoSendMaxGuests: next } : f);
                          }}
                        />
                        {maxInvalid ? (
                          <div style={{ fontSize: '0.85rem', color: '#c53030', marginTop: 4 }}>{t.autoSendMaxInvalid(limit)}</div>
                        ) : (
                          <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>
                            {sendAll ? t.autoSendAllHint : t.autoSendMaxHint}
                          </div>
                        )}
                      </div>
                      <div className="form-field">
                        <label className="form-label" htmlFor="ae-ubyportAutoSendDelayDays">{t.autoSendDelayLabel}</label>
                        <select
                          id="ae-ubyportAutoSendDelayDays"
                          className="form-control"
                          name="ubyportAutoSendDelayDays"
                          value={form.ubyportAutoSendDelayDays ?? 0}
                          onChange={e => {
                            const next = Number(e.target.value);
                            setForm(f => f ? { ...f, ubyportAutoSendDelayDays: next } : f);
                          }}
                        >
                          <option value={0}>{t.autoSendDelay0}</option>
                          <option value={1}>{t.autoSendDelay1}</option>
                          <option value={2}>{t.autoSendDelay2}</option>
                          <option value={AUTO_SEND_DELAY_MAX_DAYS}>{t.autoSendDelay3}</option>
                        </select>
                        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: 4 }}>{t.autoSendDelayHint}</div>
                        {form.ubyportAutoSendDelayDays === AUTO_SEND_DELAY_MAX_DAYS && (
                          <div role="alert" style={{ fontSize: '0.85rem', color: '#c05621', background: '#fffaf0', border: '1px solid #f6ad55', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
                            ⚠ {t.autoSendDelayWarning}
                          </div>
                        )}
                      </div>
                    </div>
                  </AdvancedDetails>
                </div>
              );
            })()}
            </div>
          </div>

          {/* ── Dorucenka bulk download ──────────────────────────────── */}
          <details id="dorucenka-download" className="ae-cx ae-cx-nested ae-cx-details">
            <summary className="ae-cx-summary">
              <div className="ae-cx-text">
                <h4 className="ae-cx-title">{t.dorucenkaHeader}</h4>
                <small className="ae-cx-sum">{nav.sumDorucenky}</small>
              </div>
              <span className="ae-cx-aside">
                <span className="ae-pill">{nav.chipOnDemand}</span>
                <span className="ae-cx-chevron" aria-hidden="true">⌄</span>
              </span>
            </summary>
            <div className="ae-cx-body">
            <div style={{ fontSize: '0.88rem', color: '#718096', marginBottom: 14 }}>
              {t.dorucenkaDesc}
            </div>

            {jobError && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: '0.88rem' }}>
                {jobError}
              </div>
            )}

            {/* ── Date range picker + request button ── */}
            {(!downloadJob || ['READY', 'FAILED', 'EXPIRED'].includes(downloadJob.status)) && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                  <label className="form-label" style={{ marginBottom: 4 }}>{t.dorucenkaLabelFrom}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)}
                    min={sixYearsAgo()}
                    max={dateTo}
                    style={{ width: 155 }}
                  />
                </div>
                <div>
                  <label className="form-label" style={{ marginBottom: 4 }}>{t.dorucenkaLabelTo}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    min={dateFrom}
                    style={{ width: 155 }}
                  />
                </div>
                <button
                  type="button"
                  className="app-btn app-btn-green app-btn-standard"
                  onClick={handleRequestDownload}
                  disabled={requesting || !dateFrom || !dateTo}
                >
                  {requesting ? t.dorucenkaRequesting : t.dorucenkaRequest}
                </button>
              </div>
            )}

            {/* ── Job status card ── */}
            {downloadJob && (() => {
              const { status, documentCount, requestedAt, completedAt, expiresAt, errorMessage, dateFrom: jFrom, dateTo: jTo } = downloadJob;

              if (status === 'PENDING' || status === 'PROCESSING') {
                return (
                  <div style={{ padding: '14px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10 }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                      {status === 'PENDING' ? t.dorucenkaPending : t.dorucenkaProcessing}
                    </div>
                    <div style={{ fontSize: '0.87rem', color: '#78350f' }}>
                      {t.dorucenkaLabelRange} {formatLocalDate(jFrom)} – {formatLocalDate(jTo)}<br />
                      {t.dorucenkaLabelRequested} {formatDateTime(requestedAt)}<br />
                      {t.dorucenkaEmailNotice}
                    </div>
                  </div>
                );
              }

              if (status === 'READY') {
                return (
                  <div style={{ padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                    <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6 }}>{t.dorucenkaReadyHeader}</div>
                    <div style={{ fontSize: '0.87rem', color: '#14532d', marginBottom: 12 }}>
                      {t.dorucenkaLabelRange} {formatLocalDate(jFrom)} – {formatLocalDate(jTo)} &nbsp;·&nbsp;
                      {t.dorucenkaFiles(documentCount ?? 0)}<br />
                      {t.dorucenkaLabelReady} {completedAt ? formatDateTime(completedAt) : '—'} &nbsp;·&nbsp;
                      {t.dorucenkaLabelExpires} {expiresAt ? formatDateTime(expiresAt) : '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="app-btn app-btn-green app-btn-standard"
                        onClick={handleDownloadZip}
                        disabled={downloading}
                      >
                        {downloading ? t.dorucenkaDownloading : t.dorucenkaDownload}
                      </button>
                      <button
                        type="button"
                        className="app-btn app-btn-standard"
                        style={{ background: '#e2e8f0', color: '#2d3748' }}
                        onClick={() => { setDownloadJob(null); setJobError(null); }}
                      >
                        {t.dorucenkaNewRequest}
                      </button>
                    </div>
                  </div>
                );
              }

              if (status === 'FAILED') {
                return (
                  <div style={{ padding: '14px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
                    <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>{t.dorucenkaFailedHeader}</div>
                    <div style={{ fontSize: '0.87rem', color: '#7f1d1d', marginBottom: 10 }}>
                      {errorMessage || 'An unexpected error occurred.'}<br />
                      <span style={{ color: '#9ca3af' }}>{t.dorucenkaLabelRequested} {formatDateTime(requestedAt)}</span>
                    </div>
                    <button
                      type="button"
                      className="app-btn app-btn-standard"
                      style={{ background: '#e2e8f0', color: '#2d3748' }}
                      onClick={() => { setDownloadJob(null); setJobError(null); }}
                    >
                      {t.dorucenkaTryAgain}
                    </button>
                  </div>
                );
              }

              if (status === 'EXPIRED') {
                return (
                  <div style={{ padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: '0.87rem', color: '#6b7280' }}>
                    {t.dorucenkaExpired(formatLocalDate(jFrom), formatLocalDate(jTo))}
                    {' '}{t.dorucenkaExpiredHint}
                  </div>
                );
              }

              return null;
            })()}
            </div>
          </details>
        </div>
      ))}

      {/* ── Payments ─────────────────────────────────────────────────────────────────────────
          One connected account per purpose — city tax, extras (extra hours, luggage) and deposit —
          each the same account or a different one (issue #114). It has its own tab because it is a
          precondition for all three, and burying it inside city tax meant a manager who does not
          collect city tax never saw it — their guests met "payment is temporarily unavailable" and
          nothing told the manager why. A change applies to future payments only: a payment or
          deposit already at Stripe stays on the account it was created on. */}
      {isShown('payments') && sec('payments', t.tabPayments, (
        <div>
          <div style={{ marginBottom: 16, background: '#f7fafc', padding: 12, borderRadius: 8, color: '#2d3748' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.paymentsHeader}</div>
            <div style={{ fontSize: '0.92rem', color: '#4a5568' }}>{t.paymentsDesc}</div>
          </div>

          {(() => {
            const stripeAccounts = paymentMethods.filter(pm => pm.type === 'STRIPE');

            if (stripeAccounts.length === 0) {
              return (
                <div style={{ padding: '12px 14px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 10, fontSize: '0.87rem', color: '#854d0e' }}>
                  ⚠️ {t.cityTaxPaymentMethodEmpty}{' '}
                  <a href="/profile?tab=payment-methods" style={{ color: '#2563eb', fontWeight: 600 }}>
                    {t.cityTaxStripeConnectLink}
                  </a>
                </div>
              );
            }

            // One row per purpose. `warnWhenNone` is whether an empty picker is already a problem
            // on this screen: for city tax only once collection is switched on. Extras and deposit
            // have their own tabs with their own switches, so for them the hint under the picker
            // says what an empty one means rather than raising a warning for a feature that may
            // not be in use.
            const purposes: {
              name: 'paymentMethodId' | 'upsellPaymentMethodId' | 'depositPaymentMethodId';
              id?: number; label: string; hint: string; none: string;
              notReady: string; warnWhenNone: boolean;
            }[] = [
              { name: 'paymentMethodId', id: form.paymentMethodId, label: t.paymentsPurposeCityTax,
                hint: t.paymentsPurposeCityTaxHint, none: t.paymentsNoneCityTax,
                notReady: t.cityTaxPaymentMethodNotReady, warnWhenNone: !!form.cityTaxCollectionEnabled },
              { name: 'upsellPaymentMethodId', id: form.upsellPaymentMethodId, label: t.paymentsPurposeUpsell,
                hint: t.paymentsPurposeUpsellHint, none: t.paymentsNoneUpsell,
                notReady: t.paymentsPurposeNotReady, warnWhenNone: false },
              { name: 'depositPaymentMethodId', id: form.depositPaymentMethodId, label: t.paymentsPurposeDeposit,
                hint: t.paymentsPurposeDepositHint, none: t.paymentsNoneDeposit,
                notReady: t.paymentsPurposeNotReady, warnWhenNone: false },
            ];

            return (
              <div style={{ display: 'grid', gap: 12 }}>
                {purposes.map(p => {
                  const selected = stripeAccounts.find(pm => pm.id === p.id) || null;
                  const ready = !!selected?.stripeChargesEnabled;
                  const warn = selected ? !ready : p.warnWhenNone;
                  return (
                    <div key={p.name} style={{
                      padding: 16, borderRadius: 10,
                      border: `2px solid ${ready ? '#48bb78' : '#e2e8f0'}`,
                      background: ready ? '#f0fff4' : '#f8fafc',
                    }}>
                      <div className="form-field" style={{ margin: 0 }}>
                        <label className="form-label">{p.label}</label>
                        <select className="form-control" name={p.name} value={p.id || 0} onChange={onChange}>
                          <option value={0}>{t.cityTaxPaymentMethodNone}</option>
                          {stripeAccounts.map(pm => (
                            <option key={pm.id} value={pm.id}>{pm.stripeChargesEnabled ? '✓ ' : '⚠ '}{pm.name}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 6 }}>{p.hint}</div>
                      </div>

                      {warn && (
                        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, fontSize: '0.8rem', color: '#854d0e' }}>
                          ⚠️ {selected ? p.notReady : p.none}{' '}
                          <a href="/profile?tab=payment-methods" style={{ color: '#2563eb', fontWeight: 600 }}>
                            {t.cityTaxStripeConnectLink}
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}

                {platformFee && (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    {t.cityTaxApplicationFeeNote.replace(
                      '{rate}',
                      `${platformFee.percentage} % (min. ${platformFee.flatAmountCzk} Kč)`
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      ))}

      {isShown('citytax') && sec('citytax', t.tabCityTax, (
        <div>
          <div style={{ marginBottom: 16, background: '#f7fafc', padding: 12, borderRadius: 8, color: '#2d3748' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.cityTaxHeader}</div>
            <div style={{ fontSize: '0.92rem', color: '#4a5568' }}>
              {t.cityTaxDesc}
            </div>
          </div>

          {/* ── Online collection via Stripe — toggle and payment method live in one card so it's ── */}
          {/* ── clear the two are tied together: picking a method only matters once this is on.  ── */}
          <div id="ae-cityTaxCollection" style={{
            marginBottom: 20, padding: 16, borderRadius: 10, transition: 'all 0.15s',
            border: `2px solid ${form.cityTaxCollectionEnabled ? '#48bb78' : '#e2e8f0'}`,
            background: form.cityTaxCollectionEnabled ? '#f0fff4' : '#f8fafc',
          }}>
            <div
              // No longer clears the account. It used to, because the picker lived inside this
              // toggle and a hidden selection could not be changed or removed — but the account is
              // not city tax's to drop: switching city tax off would silently stop the apartment
              // being able to charge for extra hours too, with nothing on screen saying so.
              onClick={() => setForm(f => (
                f ? { ...f, cityTaxCollectionEnabled: !f.cityTaxCollectionEnabled } : f
              ))}
              style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
            >
              <div style={{
                width: 44, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
                background: form.cityTaxCollectionEnabled ? '#48bb78' : '#cbd5e0', transition: 'background 0.15s',
              }}>
                <div style={{
                  position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
                  background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s',
                  left: form.cityTaxCollectionEnabled ? 23 : 3,
                }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: form.cityTaxCollectionEnabled ? '#276749' : '#2d3748' }}>
                  {t.cityTaxCollectionLabel} &nbsp;
                  <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    {form.cityTaxCollectionEnabled ? t.cityTaxCollectionEnabledText : t.cityTaxCollectionDisabledText}
                  </span>
                </div>
                <div style={{ fontSize: '0.82rem', marginTop: 2, color: form.cityTaxCollectionEnabled ? '#2f855a' : '#718096' }}>
                  {t.cityTaxCollectionHint}
                </div>
              </div>
            </div>

            {/* The account itself now lives on the Payments tab, because it is not a city tax
                setting — paid extra hours charge against the same account, and while it was nested
                in here a manager who does not collect city tax had no reason to ever open the
                screen that configures their payments. What is left here is the dependency. */}
            {form.cityTaxCollectionEnabled && (() => {
              const selected = paymentMethods.find(pm => pm.type === 'STRIPE' && pm.id === form.paymentMethodId) || null;
              const ready = !!selected?.stripeChargesEnabled;
              return (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: '0.8rem',
                  background: ready ? '#f0fdf4' : '#fefce8',
                  border: `1px solid ${ready ? '#bbf7d0' : '#fde047'}`,
                  color: ready ? '#166534' : '#854d0e',
                }}>
                  {ready
                    ? <>✓ {t.cityTaxPaysToAccount.replace('{account}', selected!.name)}</>
                    : <>⚠️ {selected ? t.cityTaxPaymentMethodNotReady : t.cityTaxNeedsAccount}</>}
                  {' '}
                  <button
                    type="button" onClick={() => switchTab('payments')}
                    style={{
                      background: 'none', border: 'none', padding: 0, font: 'inherit',
                      color: '#2563eb', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    {t.cityTaxOpenPayments}
                  </button>
                </div>
              );
            })()}
            {form.cityTaxCollectionEnabled && platformFee && (
              <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#94a3b8' }}>
                {t.cityTaxApplicationFeeNote.replace(
                  '{rate}',
                  `${platformFee.percentage} % (min. ${platformFee.flatAmountCzk} Kč)`
                )}
              </div>
            )}
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">{t.fieldAdultRate}</label>
              <input
                id="ae-cityTaxAdult"
                className="form-control"
                type="number"
                min={0}
                step={0.01}
                name="cityTaxAdult"
                value={form.cityTaxAdult ?? ''}
                onChange={onChange}
                placeholder="e.g. 50.00"
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldChildRate}</label>
              <input
                className="form-control"
                type="number"
                min={0}
                step={0.01}
                name="cityTaxChild"
                value={form.cityTaxChild ?? ''}
                onChange={onChange}
                placeholder="e.g. 25.00"
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldDisabledRate}</label>
              <input
                className="form-control"
                type="number"
                min={0}
                step={0.01}
                name="cityTaxDisabled"
                value={form.cityTaxDisabled ?? ''}
                onChange={onChange}
                placeholder="e.g. 0.00"
              />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <a
              href={`/apartments/${apartmentId}/city-tax`}
              style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}
            >
              {t.viewCityTaxReport}
            </a>
          </div>

          <AdvancedDetails title={nav.advanced} count={5}>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-field">
              <label className="form-label">{t.fieldCityTaxBankAccount}</label>
              <input id="ae-cityTaxBankAccount" className="form-control" name="cityTaxBankAccount" value={form.cityTaxBankAccount || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldCityTaxVariableSymbol}</label>
              <input id="ae-cityTaxVariableSymbol" className="form-control" name="cityTaxVariableSymbol" value={form.cityTaxVariableSymbol || ''} onChange={onChange} />
            </div>
            <div className="form-field">
              <label className="form-label">{t.fieldCityTaxContact}</label>
              <select className="form-control" name="cityTaxContactId" value={form.cityTaxContactId || 0} onChange={onChange}>
                <option value={0}>{t.cityTaxContactNone}</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.companyName ? ` — ${c.companyName}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Mailbox selector */}
          <div style={{ marginBottom: 16 }}>
            <div className="form-field">
              <label className="form-label">{t.fieldCityTaxMailbox}</label>
              <select id="ae-cityTaxMailboxId" className="form-control" name="cityTaxMailboxId" value={form.cityTaxMailboxId || 0} onChange={onChange}>
                <option value={0}>{t.cityTaxMailboxNone}</option>
                {mailboxes.map(m => (
                  <option key={m.id} value={m.id}>{m.district ? `${m.district} — ` : ''}{m.name}</option>
                ))}
                <option value={-1}>{t.cityTaxMailboxOther}</option>
              </select>
            </div>
            {/* Predefined mailbox preview */}
            {(form.cityTaxMailboxId ?? 0) > 0 && (() => {
              const mb = mailboxes.find(m => m.id === form.cityTaxMailboxId);
              return mb ? (
                <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.85rem', color: '#276749' }}>
                  <div><strong>{mb.name}</strong></div>
                  {mb.address && <div>{mb.address}</div>}
                  {mb.databoxId && <div>ID DS: {mb.databoxId}</div>}
                </div>
              ) : null;
            })()}
            {/* Other — manual fields */}
            {(form.cityTaxMailboxId ?? 0) === -1 && (
              <div style={{ marginTop: 10, padding: '12px 14px', background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 8 }}>
                <div style={{ fontSize: '0.85rem', color: '#744210', marginBottom: 12 }}>
                  Vyplňte údaje o úřadu, na který zasíláte hlášení o místním poplatku. ID datové schránky najdete ve vyhledávači na{' '}
                  <a href="https://www.mojedatovaschranka.cz/sds/search" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                    mojedatovaschranka.cz ↗
                  </a>
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div className="form-field" style={{ margin: 0 }}>
                    <label className="form-label">{t.fieldCityTaxMailboxOtherName}</label>
                    <input className="form-control" name="cityTaxMailboxOtherName" value={form.cityTaxMailboxOtherName || ''} onChange={onChange} />
                  </div>
                  <div className="form-field" style={{ margin: 0 }}>
                    <label className="form-label">{t.fieldCityTaxMailboxOtherDataboxId}</label>
                    <input className="form-control" name="cityTaxMailboxOtherDataboxId" value={form.cityTaxMailboxOtherDataboxId || ''} onChange={onChange} />
                  </div>
                  <div className="form-field" style={{ margin: 0 }}>
                    <label className="form-label">{t.fieldCityTaxMailboxOtherAddress}</label>
                    <input className="form-control" name="cityTaxMailboxOtherAddress" value={form.cityTaxMailboxOtherAddress || ''} onChange={onChange} />
                  </div>
                </div>
              </div>
            )}
          </div>
          </AdvancedDetails>
        </div>
      ), false, {
        summary: nav.sumCityTax(form.cityTaxAdult == null ? '—' : String(form.cityTaxAdult), !!form.cityTaxCollectionEnabled),
        chip: form.cityTaxCollectionEnabled ? { tone: 'ok', text: nav.chipOnline } : { tone: 'off', text: nav.chipOff },
      })}

      {isShown('calendar') && sec('calendar', t.tabCalendar, (
        <div>
          <CalendarManager apartmentId={apartmentId} />
        </div>
      ))}

      {isShown('contact') && sec('contact', t.tabContact, (
        <div>
          {/* ── Assigned contact ────────────────────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#2d3748' }}>{t.contactHeader}</div>
            <div style={{ fontSize: '0.88rem', color: '#718096', marginBottom: 12 }}>
              {t.contactDesc}
            </div>
            <div id="ae-contactSource" style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e0', width: 'fit-content', marginBottom: 12 }}>
              {(['manager', 'list', 'other'] as const).map((option, i) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setContactSource(option);
                    if (option !== 'list') {
                      setForm(f => f ? { ...f, assignedContact: undefined } : f);
                    }
                    if (option !== 'other') {
                      setForm(f => f ? { ...f, contactFullName: '', contactEmail: '', contactPhone: '' } : f);
                    }
                  }}
                  style={{
                    padding: '7px 16px',
                    border: 'none',
                    borderRight: i < 2 ? '1px solid #cbd5e0' : 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    background: contactSource === option ? 'var(--brand)' : '#fff',
                    color: contactSource === option ? '#fff' : '#4a5568',
                    transition: 'background 0.15s',
                  }}
                >
                  {option === 'manager' ? t.contactSourceManager : option === 'list' ? t.contactSourceList : t.contactSourceOther}
                </button>
              ))}
            </div>
            {contactSource === 'list' && (
              <>
                <select className="form-control" name="contactId" value={form.assignedContact?.id || 0} onChange={onChange}>
                  <option value={0}>{t.selectContact}</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {contactEditForm && (
                  <div style={{ marginTop: 14, padding: '14px 16px', background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#2d3748', marginBottom: 8 }}>{t.editContactDetails}</div>
                    <div style={{ fontSize: '0.88rem', color: '#4a5568', lineHeight: 1.9 }}>
                      {contactEditForm.name && <div><strong>{contactEditForm.name}</strong></div>}
                      {contactEditForm.companyName && <div>{contactEditForm.companyName}</div>}
                      {contactEditForm.email && <div>📧 {contactEditForm.email}</div>}
                      {contactEditForm.phoneNumber && <div>📞 {contactEditForm.phoneNumber}</div>}
                      {contactEditForm.ico && <div>IČO: {contactEditForm.ico}</div>}
                      {contactEditForm.address && <div>{contactEditForm.address}</div>}
                    </div>
                    <div style={{ marginTop: 8, fontSize: '0.8rem' }}>
                      <Link to="/profile" state={{ tab: 'contacts' }} style={{ color: 'var(--brand)', fontWeight: 600 }}>{t.contactReadOnlyHint}</Link>
                    </div>
                  </div>
                )}
              </>
            )}
            {contactSource === 'other' && (
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">{t.fieldContactFullName}</label>
                  <input className="form-control" name="contactFullName" value={form.contactFullName || ''} onChange={onChange} />
                </div>
                <div className="form-field">
                  <label className="form-label">{t.fieldContactEmail}</label>
                  <input className="form-control" type="email" name="contactEmail" value={form.contactEmail || ''} onChange={onChange} />
                </div>
                <div className="form-field">
                  <label className="form-label">{t.fieldContactPhone}</label>
                  <input className="form-control" name="contactPhone" value={form.contactPhone || ''} onChange={onChange} />
                </div>
              </div>
            )}
          </div>

          {/* ── Guest form footer ────────────────────────────────────────────── */}
          <AdvancedDetails title={t.footerHeader} count={3}>
            <div style={{ fontSize: '0.88rem', color: '#718096', marginBottom: 12 }}>
              {t.footerDesc}
            </div>
            <div className="form-grid">
              <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">{t.fieldFooterType}</label>
                <select
                  id="ae-guestFooterContactType"
                  className="form-control"
                  name="guestFooterContactType"
                  value={form.guestFooterContactType === 'host' ? 'manager' : (form.guestFooterContactType || 'manager')}
                  onChange={onChange}
                >
                  <option value="manager">{t.footerTypeManager}</option>
                  <option value="contact">{t.footerTypeContact}</option>
                </select>
              </div>
              {(form.guestFooterContactType || 'manager') === 'contact' && (
                <>
                  <div className="form-field">
                    <label className="form-label">{t.footerContactFullName}</label>
                    <input className="form-control" name="contactFullName" value={form.contactFullName || ''} onChange={onChange} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.footerContactEmail}</label>
                    <input className="form-control" type="email" name="contactEmail" value={form.contactEmail || ''} onChange={onChange} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.footerContactPhone}</label>
                    <input className="form-control" name="contactPhone" value={form.contactPhone || ''} onChange={onChange} />
                  </div>
                </>
              )}
            </div>
          </AdvancedDetails>
        </div>
      ))}

      {isShown('guide') && sec('guide', t.tabGuide, (
        <div>
          {!guideLoaded && <SectionLoader label={t.loadingGuide} minHeight={240} />}
          {guideLoaded && (
          <>
          {/* Variant 4 of #132: five groups instead of one long form, one open at a time, each with a
              line saying what is set. The "show to guests" switches sit inside the group they
              govern instead of in a block of eleven checkboxes at the top. */}
          <div className="ae-gtoolbar">
            <label htmlFor="guideEnabled">
              <input type="checkbox" id="guideEnabled" checked={guide.enabled}
                onChange={e => setGuide(g => ({ ...g, enabled: e.target.checked }))} />
              <span>{t.guideEnable}</span>
            </label>
            <small>{guide.enabled ? nav.guideHelpOn : nav.guideHelpOff}</small>
            <button type="button" className="ae-btn" onClick={() => setGuidePreviewOpen(true)}
              title={guidePreviewText[lang].previewButtonHint} aria-label={`${guidePreviewText[lang].previewButton}. ${guidePreviewText[lang].previewButtonHint}`}
              style={{ minHeight: 44 }}>
              {guidePreviewText[lang].previewButton}
            </button>
          </div>
          {guidePreviewOpen && (
            <GuidePreviewModal config={guide} apartmentName={form?.name || ''} onClose={() => setGuidePreviewOpen(false)} />
          )}

          <div className="ae-gacc">
            {guideGroup('arrival', '📍', nav.ggArrival, nav.gsArrDep(guide.address || nav.gsNoAddress, guide.checkInTime || '', guide.checkOutTime || ''), (
              <>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldCheckinTime}</label>
                    {/* step=1800 (30 min): StayExtensionStair walks outward from this exact value in
                        30-minute slots (#94 part 2), so an off-grid standard time would make every
                        upsell option the guest sees land on an equally off-grid time. The backend
                        rejects anything finer; this just steers the picker there first. */}
                    <input id="ae-guide-checkInTime" className="form-control" type="time" step={1800} value={guide.checkInTime || ''}
                      onChange={e => setGuide(g => ({ ...g, checkInTime: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldCheckoutTime}</label>
                    <input id="ae-guide-checkOutTime" className="form-control" type="time" step={1800} value={guide.checkOutTime || ''}
                      onChange={e => setGuide(g => ({ ...g, checkOutTime: e.target.value }))} />
                  </div>
                </div>
                {guideSwitch('showAddress', t.guideSecAddress)}
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t.guideFieldAddress}</label>
                    <input id="ae-guide-address" className="form-control" value={guide.address || ''}
                      onChange={e => setGuide(g => ({ ...g, address: e.target.value }))}
                      placeholder="Full address shown to guests" />
                  </div>
                  <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t.guideFieldAddressRemark}</label>
                    <textarea id="ae-guide-addressRemark" className="form-control" rows={2} value={guide.addressRemark || ''}
                      onChange={e => setGuide(g => ({ ...g, addressRemark: e.target.value }))}
                      placeholder={t.guideAddressRemarkPlaceholder} />
                    <GuideEnglishField fieldKey="addressRemark" rows={2} guide={guide} setGuide={setGuide}
                      openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                      addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                  </div>
                </div>
              </>
            ))}

            {guideGroup('access', '🔑', nav.ggAccess, guideAccessSummary, (
              <>
                {guideSwitch('showKeyAccess', t.guideSecKeyAccess)}
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldAccessType}</label>
                    <select id="ae-guide-keyAccessType" className="form-control" value={guide.keyAccessType || ''}
                      onChange={e => {
                        const val = e.target.value as any || null;
                        setGuide(g => ({ ...g, keyAccessType: val }));
                        setForm(f => f ? { ...f, ttlockEnabled: val === 'TTLOCK' } : f);
                      }}>
                      <option value="">{t.guideAccessTypeSelect}</option>
                      <option value="DOOR_CODE">{t.guideAccessTypeDoorCode}</option>
                      <option value="KEYBOX">{t.guideAccessTypeKeybox}</option>
                      <option value="PERSON">{t.guideAccessTypePerson}</option>
                      <option value="NONE">{t.guideAccessTypeNoKey}</option>
                      <option value="TTLOCK">{t.guideAccessTypeTtlock}</option>
                    </select>
                  </div>
                  {guide.keyAccessType === 'TTLOCK' && (
                    <div className="form-field">
                      <label className="form-label">{t.guideFieldSmartLock}</label>
                      <select className="form-control"
                        value={form?.ttlockLockId ?? ''}
                        onChange={e => setForm(f => f ? { ...f, ttlockLockId: e.target.value ? Number(e.target.value) : null } : f)}>
                        <option value="">{t.guideSelectLock}</option>
                        {ttlockLocks.map(lock => (
                          <option key={lock.id} value={lock.lockId}>{lock.lockAlias || lock.lockName}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* The apartment's own checkin/checkoutTime fields were removed in #94 — the
                      check-in guide is now the only place the standard times are written, and the
                      smart-lock PIN window is its own thing (#95). */}
                  {(guide.keyAccessType === 'DOOR_CODE' || guide.keyAccessType === 'KEYBOX') && (
                    <div className="form-field">
                      <label className="form-label">{t.guideFieldKeyCode}</label>
                      <input id="ae-guide-keyCode" className="form-control" value={guide.keyCode || ''}
                        onChange={e => setGuide(g => ({ ...g, keyCode: e.target.value }))}
                        placeholder={t.guideKeyCodePlaceholder} />
                    </div>
                  )}
                  {(guide.keyAccessType === 'TTLOCK' || ((guide.keyAccessType === 'DOOR_CODE' || guide.keyAccessType === 'KEYBOX') && guide.keyCode && guide.keyCode.trim())) && (
                    <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">{guide.keyAccessType === 'TTLOCK' ? t.guidePinVisibleFrom : t.guideCodeVisibleFrom}</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                          <input type="radio" name="keyCodeVisibility" checked={guide.keyCodeVisibleFrom === null}
                            onChange={() => setGuide(g => ({ ...g, keyCodeVisibleFrom: null }))} />
                          {t.guideVisibilityAlways}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                          <input type="radio" name="keyCodeVisibility" checked={guide.keyCodeVisibleFrom !== null}
                            onChange={() => setGuide(g => ({ ...g, keyCodeVisibleFrom: g.keyCodeVisibleFrom ?? '12:00' }))} />
                          {t.guideVisibilityFrom}
                        </label>
                        {guide.keyCodeVisibleFrom !== null && (
                          <input type="time" className="form-control" style={{ width: 120 }}
                            value={guide.keyCodeVisibleFrom || '12:00'}
                            onChange={e => setGuide(g => ({ ...g, keyCodeVisibleFrom: e.target.value }))} />
                        )}
                      </div>
                      {guide.keyCodeVisibleFrom !== null && (
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>
                          {guide.keyAccessType === 'TTLOCK'
                            ? t.guidePinHiddenUntil(guide.keyCodeVisibleFrom || '')
                            : t.guideCodeHiddenUntil(guide.keyCodeVisibleFrom || '')}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t.guideFieldKeyRemark}</label>
                    <textarea id="ae-guide-keyRemark" className="form-control" rows={2} value={guide.keyRemark || ''}
                      onChange={e => setGuide(g => ({ ...g, keyRemark: e.target.value }))}
                      placeholder={t.guideKeyRemarkPlaceholder} />
                    <GuideEnglishField fieldKey="keyRemark" rows={2} guide={guide} setGuide={setGuide}
                      openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                      addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                  </div>
                </div>
                {guide.keyAccessType === 'TTLOCK' && (
                  <AdvancedDetails title={nav.advancedLock} count={2}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="app-btn app-btn-standard"
                        style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2' }}
                        disabled={ttlockClearingPins || !form?.ttlockLockId}
                        onClick={async () => {
                          setTtlockClearingPins(true);
                          setTtlockPinsClearedMsg(null);
                          setTtlockError(null);
                          try {
                            const result = await clearTtlockPins(apartmentId, getToken());
                            setTtlockPinsClearedMsg(
                              `${result.clearedCount} upcoming reservation PIN(s) cleared. The system will re-generate them automatically.`
                            );
                          } catch (e: any) {
                            setTtlockError(e.response?.data?.message || e.message || 'Failed to clear PINs');
                          } finally {
                            setTtlockClearingPins(false);
                          }
                        }}
                      >
                        {ttlockClearingPins ? `Clearing… ${ttlockClearingRemaining > 0 ? ttlockClearingRemaining + 's' : '…'}` : 'Reset all PINs'}
                      </button>
                      <button
                        type="button"
                        className="app-btn app-btn-blue app-btn-standard"
                        disabled={ttlockGeneratingPins || !form?.ttlockLockId}
                        onClick={async () => {
                          setTtlockGeneratingPins(true);
                          setTtlockPinsGeneratedMsg(null);
                          setTtlockError(null);
                          try {
                            const result = await runTtlockGenerationJob(apartmentId, getToken());
                            setTtlockPinsGeneratedMsg(
                              `${result.generatedCount} reservation(s) got a new PIN.`
                            );
                          } catch (e: any) {
                            setTtlockError(e.response?.data?.message || e.message || 'Failed to run generation job');
                          } finally {
                            setTtlockGeneratingPins(false);
                          }
                        }}
                      >
                        {ttlockGeneratingPins ? `Generating… ${ttlockGeneratingRemaining > 0 ? ttlockGeneratingRemaining + 's' : '…'}` : 'Run generation job'}
                      </button>
                    </div>
                    {ttlockError && <div style={{ color: '#c53030', fontSize: '0.85rem', marginTop: 4 }}>{ttlockError}</div>}
                    {ttlockPinsClearedMsg && (
                      <div style={{ color: '#276749', fontSize: '0.85rem', marginTop: 4 }}>
                        {ttlockPinsClearedMsg}
                      </div>
                    )}
                    {ttlockPinsGeneratedMsg && (
                      <div style={{ color: '#276749', fontSize: '0.85rem', marginTop: 4 }}>
                        {ttlockPinsGeneratedMsg}
                      </div>
                    )}

                  </AdvancedDetails>
                )}
              </>
            ))}

            {guideGroup('stay', '📶', nav.ggStay, guideStaySummary, (
              <>
                {guideSwitch('showWifi', t.guideSecWifi)}
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldWifiName}</label>
                    <input id="ae-guide-wifiName" className="form-control" value={guide.wifiName || ''}
                      onChange={e => setGuide(g => ({ ...g, wifiName: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldWifiPassword}</label>
                    <input id="ae-guide-wifiPassword" className="form-control" value={guide.wifiPassword || ''}
                      onChange={e => setGuide(g => ({ ...g, wifiPassword: e.target.value }))} />
                  </div>
                </div>
                {guideSwitch('showParking', t.guideSecParking)}
                <div className="form-grid">
                  <div className="form-field" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">{t.guideFieldParkingInfo}</label>
                    <textarea id="ae-guide-parkingInfo" className="form-control" rows={2} value={guide.parkingInfo || ''}
                      onChange={e => setGuide(g => ({ ...g, parkingInfo: e.target.value }))}
                      placeholder={t.guideParkingPlaceholder} />
                    <GuideSampleControl field="parkingInfo" value={guide.parkingInfo} onChange={text => setGuide(g => ({ ...g, parkingInfo: text }))} />
                    <GuideEnglishField fieldKey="parkingInfo" rows={2} guide={guide} setGuide={setGuide}
                      openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                      addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.guideFieldParkingPrice}</label>
                    <input className="form-control" value={guide.parkingPrice || ''}
                      onChange={e => setGuide(g => ({ ...g, parkingPrice: e.target.value }))}
                      placeholder={t.guideParkingPricePlaceholder} />
                  </div>
                </div>
                {guideSwitch('showContact', t.guideSecHostContact)}
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">{t.guideHostName}</label>
                    <input id="ae-guide-hostContactName" className="form-control" value={guide.hostContactName || ''}
                      onChange={e => setGuide(g => ({ ...g, hostContactName: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.guideHostPhone}</label>
                    <input className="form-control" type="tel" value={guide.hostContactPhone || ''}
                      onChange={e => setGuide(g => ({ ...g, hostContactPhone: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{t.guideHostEmail}</label>
                    <input className="form-control" type="email" value={guide.hostContactEmail || ''}
                      onChange={e => setGuide(g => ({ ...g, hostContactEmail: e.target.value }))} />
                  </div>
                </div>
                {guideSwitch('showWeather', t.guideSecWeather)}
              </>
            ))}

            {guideGroup('content', '📖', nav.ggContent, guideContentSummary, (
              <>
                {guideSwitch('showDetailedGuide', t.guideSecDetailedGuide)}
                <div className="form-field">
                  <textarea id="ae-guide-detailedGuide" className="form-control" rows={6} value={guide.detailedGuide || ''}
                    aria-label={stripLeadingSymbols(t.guideSecDetailedGuide)}
                    onChange={e => setGuide(g => ({ ...g, detailedGuide: e.target.value }))}
                    placeholder={t.guideDetailedGuidePlaceholder} />
                  <GuideSampleControl field="detailedGuide" value={guide.detailedGuide} onChange={text => setGuide(g => ({ ...g, detailedGuide: text }))} />
                  <GuideEnglishField fieldKey="detailedGuide" rows={6} guide={guide} setGuide={setGuide}
                    openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                    addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                </div>
                {guideSwitch('showAttentionNotes', t.guideSecAttentionNotes)}
                <div className="form-field">
                  <textarea id="ae-guide-attentionNotes" className="form-control" rows={4} value={guide.attentionNotes || ''}
                    aria-label={stripLeadingSymbols(t.guideSecAttentionNotes)}
                    onChange={e => setGuide(g => ({ ...g, attentionNotes: e.target.value }))}
                    placeholder={t.guideAttentionPlaceholder} />
                  <GuideSampleControl field="attentionNotes" value={guide.attentionNotes} onChange={text => setGuide(g => ({ ...g, attentionNotes: text }))} />
                  <GuideEnglishField fieldKey="attentionNotes" rows={4} guide={guide} setGuide={setGuide}
                    openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                    addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                </div>
                {guideSwitch('showPhotos', t.guideSecPhotos)}
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 14px' }}>
                    {t.guidePhotosDesc}
                  </p>
            {photoError && (
              <div style={{ color: '#c53030', marginBottom: 10, fontSize: '0.88rem' }}>{photoError}</div>
            )}

            {guide.photos.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
                {guide.photos.map((photo, idx) => (
                  <div key={photo.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <img
                      src={photo.publicUrl}
                      alt={photo.caption || `Photo ${idx + 1}`}
                      style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                    />
                    {photo.caption && (
                      <div style={{ padding: '4px 6px', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', lineHeight: 1.3 }}>
                        {photo.caption}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 2, padding: '4px 6px', background: '#f1f5f9', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button
                          disabled={idx === 0}
                          title="Move left"
                          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #cbd5e0', borderRadius: 4, background: '#fff', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}
                          onClick={async () => {
                            const ids = guide.photos.map(p => p.id);
                            [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                            try {
                              await reorderGuidePhotos(apartmentId, ids, getToken());
                              setGuide(g => {
                                const photos = [...g.photos];
                                [photos[idx - 1], photos[idx]] = [photos[idx], photos[idx - 1]];
                                return { ...g, photos };
                              });
                            } catch (e: any) { setPhotoError(e.response?.data?.message || 'Reorder failed'); }
                          }}>◀</button>
                        <button
                          disabled={idx === guide.photos.length - 1}
                          title="Move right"
                          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #cbd5e0', borderRadius: 4, background: '#fff', cursor: idx === guide.photos.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === guide.photos.length - 1 ? 0.4 : 1 }}
                          onClick={async () => {
                            const ids = guide.photos.map(p => p.id);
                            [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
                            try {
                              await reorderGuidePhotos(apartmentId, ids, getToken());
                              setGuide(g => {
                                const photos = [...g.photos];
                                [photos[idx], photos[idx + 1]] = [photos[idx + 1], photos[idx]];
                                return { ...g, photos };
                              });
                            } catch (e: any) { setPhotoError(e.response?.data?.message || 'Reorder failed'); }
                          }}>▶</button>
                      </div>
                      <button
                        title="Delete photo"
                        style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #fed7d7', borderRadius: 4, background: '#fff5f5', color: '#c53030', cursor: 'pointer' }}
                        onClick={async () => {
                          if (!window.confirm(t.guidePhotoConfirmDelete)) return;
                          try {
                            await deleteGuidePhoto(apartmentId, photo.id, getToken());
                            setGuide(g => ({ ...g, photos: g.photos.filter(p => p.id !== photo.id) }));
                          } catch (e: any) { setPhotoError(e.response?.data?.message || 'Delete failed'); }
                        }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {guide.photos.length < 10 && (
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label className="form-label" style={{ fontSize: '0.82rem' }}>{t.guidePhotoCaption}</label>
                    <input
                      className="form-control"
                      value={photoCaption}
                      onChange={e => setPhotoCaption(e.target.value)}
                      placeholder={t.guidePhotoCaptionPlaceholder}
                      maxLength={300}
                      disabled={photoUploading}
                    />
                  </div>
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                    background: photoUploading ? '#e2e8f0' : '#2563eb', color: '#fff',
                    borderRadius: 6, cursor: photoUploading ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: '0.88rem', flexShrink: 0,
                  }}>
                    {photoUploading ? t.guidePhotoUploading(photoUploadProgress) : t.guidePhotoAdd}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      style={{ display: 'none' }}
                      disabled={photoUploading}
                      onChange={async (e) => {
                        const raw = e.target.files?.[0];
                        e.target.value = '';
                        if (!raw) return;
                        setPhotoError(null);
                        setPhotoUploading(true);
                        setPhotoUploadProgress(0);
                        try {
                          let file = raw;
                          const name = raw.name.toLowerCase();
                          if (raw.type === 'image/heic' || raw.type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')) {
                            const heic2any = (await import('heic2any')).default;
                            const blob = await heic2any({ blob: raw, toType: 'image/jpeg', quality: 0.9 }) as Blob;
                            file = new File([blob], raw.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
                          }
                          const uploaded = await uploadGuidePhoto(apartmentId, file, photoCaption, getToken(), setPhotoUploadProgress);
                          setGuide(g => ({ ...g, photos: [...g.photos, uploaded] }));
                          setPhotoCaption('');
                        } catch (err: any) {
                          setPhotoError(err.response?.data?.message || err.message || 'Upload failed');
                        } finally {
                          setPhotoUploading(false);
                          setPhotoUploadProgress(0);
                        }
                      }}
                    />
                  </label>
                </div>
                {photoUploading && (
                  <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{ height: '100%', width: `${photoUploadProgress}%`, background: '#2563eb', borderRadius: 3, transition: 'width 0.2s' }} />
                  </div>
                )}
                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  {t.guidePhotoFileHint(guide.photos.length)}
                </div>
              </div>
            )}
            {guide.photos.length >= 10 && (
              <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                {t.guidePhotoMaxReached}
              </div>
            )}
                </div>
                {guideSwitch('showOtherLinks', t.guideSecOtherLinks)}
                <div>
            {guide.otherLinks.map((link, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <input className="form-control" value={link.label} placeholder={t.guideLinkLabelPlaceholder}
                  style={{ flex: 1 }}
                  onChange={e => setGuide(g => ({ ...g, otherLinks: g.otherLinks.map((l, j) => j === i ? { ...l, label: e.target.value } : l) }))} />
                <input className="form-control" value={link.url} placeholder={t.guideLinkUrlPlaceholder}
                  style={{ flex: 2 }}
                  onChange={e => setGuide(g => ({ ...g, otherLinks: g.otherLinks.map((l, j) => j === i ? { ...l, url: e.target.value } : l) }))} />
                <button className="app-btn app-btn-red app-btn-standard"
                  style={{ padding: '6px 12px', flexShrink: 0 }}
                  onClick={() => setGuide(g => ({ ...g, otherLinks: g.otherLinks.filter((_, j) => j !== i) }))}>✕</button>
              </div>
            ))}
            <button className="app-btn app-btn-blue app-btn-standard"
              style={{ marginTop: 4, padding: '6px 14px', fontSize: '0.85rem' }}
              onClick={() => setGuide(g => ({ ...g, otherLinks: [...g.otherLinks, { label: '', url: '' }] }))}>
              {t.guideAddLink}
            </button>
                </div>
              </>
            ))}

            {guideGroup('departure', '🧳', nav.ggDeparture, guideDepartureSummary, (
              <>
                {guideSwitch('showCheckoutInstructions', t.guideSecCheckoutInstructions)}
                <div className="form-field">
                  <textarea id="ae-guide-checkoutInstructions" className="form-control" rows={4} value={guide.checkoutInstructions || ''}
                    aria-label={stripLeadingSymbols(t.guideSecCheckoutInstructions)}
                    onChange={e => setGuide(g => ({ ...g, checkoutInstructions: e.target.value }))}
                    placeholder={t.guideCheckoutPlaceholder} />
                  <GuideSampleControl field="checkoutInstructions" value={guide.checkoutInstructions} onChange={text => setGuide(g => ({ ...g, checkoutInstructions: text }))} />
                  <GuideEnglishField fieldKey="checkoutInstructions" rows={4} guide={guide} setGuide={setGuide}
                    openFields={guideEnFieldsOpen} setOpenFields={setGuideEnFieldsOpen}
                    addLabel={t.guideEnglishVersionAdd} fieldLabel={t.guideEnglishVersionLabel} clearLabel={t.guideEnglishVersionClear} />
                </div>
              </>
            ))}
          </div>

          {/* ── What the guide is (was a large always-open infographic) ───────── */}
          <HelpDetails title={t.guideIntroTitle}>
<div style={{ fontSize: '0.88rem', color: '#2d3748', marginBottom: 16 }}>{t.guideIntroDesc}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                  {([
                    { icon: '✏️', step: '1', label: t.guideIntroStep1Label, desc: t.guideIntroStep1Desc, bg: '#ebf8ff', border: '#90cdf4' },
                    { icon: '🔗', step: '2', label: t.guideIntroStep2Label, desc: t.guideIntroStep2Desc, bg: '#f0fff4', border: '#9ae6b4' },
                    { icon: '📱', step: '3', label: t.guideIntroStep3Label, desc: t.guideIntroStep3Desc, bg: '#fffaf0', border: '#fbd38d' },
                  ]).map(({ icon, step, label, desc, bg, border }) => (
                    <div key={step} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {step}. {label}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#4a5568', lineHeight: 1.5 }}>{desc}</div>
                    </div>
                  ))}
                </div>
          </HelpDetails>

          </>
          )}
        </div>
      ), !guideLoaded)}

      {/* Selling extra hours either side of a stay. Its own tab rather than a footnote under the
          guide: it is a pricing and approvals surface with its own workflow, and burying it under
          the guide's own long form made it read as an afterthought of guide configuration. */}
      {isShown('deposit') && apartmentId && sec('deposit', t.tabDeposit, (
        <div>
          <DepositSettings apartmentId={apartmentId} onSaved={() => facts.refresh({ money: true })} />
        </div>
      ), false, {
        summary: nav.sumDeposit(
          facts.deposit === null ? null : facts.deposit?.enabled,
          facts.deposit?.amount ? `${facts.deposit.amount.toLocaleString(lang === 'en' ? 'en-GB' : lang)} ${facts.deposit.currency || 'Kč'}` : null,
        ),
        chip: facts.deposit ? (facts.deposit.enabled ? { tone: 'ok', text: nav.chipOn } : { tone: 'off', text: nav.chipOff }) : undefined,
      })}

      {isShown('upsell') && apartmentId && sec('upsell', t.tabUpsell, (
        <div>
          <UpsellSettings apartmentId={apartmentId} onSaved={() => facts.refresh({ money: true })} />
        </div>
      ), false, {
        summary: nav.sumUpsell(
          facts.extensions === null ? null : facts.extensions?.mode,
          facts.extensions === null ? null : facts.extensions?.luggageMode,
        ),
        chip: facts.extensions
          ? (facts.extensions.mode !== 'OFF' || facts.extensions.luggageMode !== 'OFF'
            ? { tone: 'ok', text: nav.chipOn } : { tone: 'off', text: nav.chipOff })
          : undefined,
      })}

      {view === BOOKING_VIEW && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 24, marginTop: 4 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>🌐 Web přímé rezervace</h3>
          <DirectBookingTab apartmentId={apartmentId} />
        </div>
      )}

      {isShown('export') && sec('export', t.exportHeader, (
        <div>
          <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: 24, fontSize: '0.88rem', lineHeight: 1.55 }}>
            {t.exportDesc}
          </p>

          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}>{t.exportLabelFrom}</label>
                <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border-input)', borderRadius: 10, fontSize: 14, background: 'var(--bg-surface)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}>{t.exportLabelTo}</label>
                <input type="date" value={exportTo} min={exportFrom} onChange={e => setExportTo(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border-input)', borderRadius: 10, fontSize: 14, background: 'var(--bg-surface)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 420 }}>
              <DateRangePicker
                from={exportFrom}
                to={exportTo}
                onFromChange={setExportFrom}
                onToChange={setExportTo}
                labelFrom={t.exportLabelFrom}
                labelTo={t.exportLabelTo}
              />
            </div>
          )}

          {exportError && (
            <div style={{ color: 'var(--danger)', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>⚠</span>{exportError}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginTop: 16 }}>
            {/* CSV card */}
            <button
              disabled={exportLoadingType !== null || !exportFrom || !exportTo}
              onClick={async () => {
                setExportError(null);
                setExportLoadingType('csv');
                try {
                  await downloadCsv(apartmentId, exportFrom, exportTo);
                } catch (e: any) {
                  setExportError(e.message || 'Failed to export CSV');
                } finally {
                  setExportLoadingType(null);
                }
              }}
              style={{
                border: '1.5px solid #bbf7d0', borderRadius: 10, padding: 16,
                background: exportLoadingType !== null ? '#f0fdf4' : '#f0fdf4',
                cursor: exportLoadingType !== null || !exportFrom || !exportTo ? 'not-allowed' : 'pointer',
                opacity: exportLoadingType !== null || !exportFrom || !exportTo ? 0.6 : 1,
                display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.borderColor = 'var(--success)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(22,163,74,0.12)'; }}}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#bbf7d0'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(22,163,74,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="3" y1="15" x2="21" y2="15"/>
                    <line x1="9" y1="9" x2="9" y2="21"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--success)', letterSpacing: '-0.2px' }}>CSV</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20, background: 'rgba(22,163,74,0.15)', color: 'var(--success)' }}>{t.exportCsvBadge}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t.exportCsvDesc}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--success)' }}>
                {exportLoadingType === 'csv'
                  ? <><span style={{ width: 12, height: 12, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />{t.exportCsvLoading}</>
                  : <><span>↓</span>{t.exportCsv.replace('⬇ ', '')}</>}
              </div>
            </button>

            {/* PDF card */}
            <button
              disabled={exportLoadingType !== null || !exportFrom || !exportTo}
              onClick={async () => {
                setExportError(null);
                setExportLoadingType('pdf');
                try {
                  const [data, lookup] = await Promise.all([
                    fetchExportData(apartmentId, exportFrom, exportTo),
                    fetchUbyportLookup(),
                  ]);
                  const aptName = form?.name || form?.displayName || String(apartmentId);
                  generateExportPdf(data, aptName, exportFrom, exportTo, lookup);
                } catch (e: any) {
                  setExportError(e.message || 'Failed to generate PDF');
                } finally {
                  setExportLoadingType(null);
                }
              }}
              style={{
                border: '1.5px solid #feb2b2', borderRadius: 10, padding: 16,
                background: '#fff5f5',
                cursor: exportLoadingType !== null || !exportFrom || !exportTo ? 'not-allowed' : 'pointer',
                opacity: exportLoadingType !== null || !exportFrom || !exportTo ? 0.6 : 1,
                display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(220,38,38,0.12)'; }}}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#feb2b2'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--danger)', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <line x1="10" y1="9" x2="8" y2="9"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)', letterSpacing: '-0.2px' }}>PDF</span>
                <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20, background: 'rgba(220,38,38,0.1)', color: 'var(--danger)' }}>{t.exportPdfBadge}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t.exportPdfDesc}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>
                {exportLoadingType === 'pdf'
                  ? <><span style={{ width: 12, height: 12, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />{t.exportPdfLoading}</>
                  : <><span>↓</span>{t.exportPdf.replace('⬇ ', '')}</>}
              </div>
            </button>
          </div>
        </div>
      ), false, { summary: nav.sumExport, chip: { tone: 'off', text: nav.chipOnDemand } })}

      </div>

      {view === 'basic' && (
        <div className="ae-danger">
          <h3 className="ae-danger-h">{nav.dangerZone}</h3>
          <p id="ae-delete-description" className="ae-danger-description">{t.deleteApartmentDescription}</p>
          <button id="ae-deleteApartment" aria-describedby="ae-delete-description" onClick={handleDeleteApartment} disabled={saving} className="app-btn app-btn-red app-btn-standard">
            {t.deleteApartment}
          </button>
        </div>
      )}

      {saveBarRevealed && view !== BOOKING_VIEW && GROUPS_WITH_SAVE_BAR.includes(view) && (
        <SaveBar
          text={nav} unsavedGroups={unsavedGroupNames} saving={saving} savingLabel={t.saving}
          locked={guideSaving}
          onSave={() => { if (!guideSaving && !saving) { if (view === 'arrival' || guideDirty) saveGuide(); else saveApartment(); } }} onDiscard={discardChanges}
        />
      )}

      {deleteModalOpen && deleteSummary && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 28, maxWidth: 480, width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: '#c53030' }}>{t.deleteModalHeader}</h2>
            <p style={{ margin: '0 0 16px', color: '#4a5568' }}>
              {t.deleteModalDescPre} <strong>{form?.name || form?.displayName}</strong> {t.deleteModalDescPost}
            </p>

            {(deleteSummary.reservationCount > 0 || deleteSummary.dorucenkaCount > 0) && (
              <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontWeight: 600, color: '#c53030', marginBottom: 6 }}>{t.deleteModalWillDelete}</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#4a5568', fontSize: '0.95rem' }}>
                  {deleteSummary.reservationCount > 0 && (
                    <li>{t.deleteModalReservations(deleteSummary.reservationCount)}
                      {deleteSummary.ubyportSubmittedCount > 0 && (
                        <span style={{ color: '#c53030' }}> {t.deleteModalUbyportSubmitted(deleteSummary.ubyportSubmittedCount)}</span>
                      )}
                    </li>
                  )}
                  {deleteSummary.dorucenkaCount > 0 && (
                    <li>
                      <strong>{t.deleteModalDorucenka(deleteSummary.dorucenkaCount)}</strong>
                    </li>
                  )}
                  {deleteSummary.calendarCount > 0 && (
                    <li>{t.deleteModalCalendars(deleteSummary.calendarCount)}</li>
                  )}
                </ul>
              </div>
            )}

            <p style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#4a5568' }}>
              {t.deleteModalTypeToConfirm}
            </p>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={e => setDeleteConfirmName(e.target.value)}
              placeholder={form?.name || form?.displayName || ''}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid #cbd5e0', boxSizing: 'border-box', marginBottom: 16 }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteModalOpen(false); setDeleteConfirmName(''); }}
                disabled={deleting}
                className="app-btn app-btn-standard"
                style={{ background: '#e2e8f0', color: '#2d3748' }}
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting || deleteConfirmName.trim() !== (form?.name || form?.displayName || '').trim()}
                className="app-btn app-btn-red app-btn-standard"
              >
                {deleting ? t.deleting : t.deleteConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApartmentDetail;

